/**
 * Billing.tsx — Full WBSEDCL Bill Breakdown Page
 * ================================================
 * All values come directly from the Pi-computed DERIVED tab.
 * Frontend only does:
 *   - Demand Charge / ED / Meter Rental / TPR (from tariff constants)
 *   - Daily cost timeline chart (from per-row dailyCostRs)
 *   - Projected month-end (linear extrapolation from today's run rate)
 */

import { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import type { UseSheetData } from '@/hooks/useSheetData';
import { Card, CardHeader } from '@/components/ui/Card';
import { PageHeader, PageLoadingSkeleton, PageErrorState, StaleDataBanner } from '@/components/ui/PageState';
import { formatRupees, formatKwh, formatPF, formatNumber } from '@/lib/formatters';
import { buildBillBreakdown } from '@/lib/billing';
import { maxDemandKvaOf, fifteenMinDemandBlocks } from '@/lib/calculations';
import { useTariff } from '@/contexts/TenantContext';
import type { PfAdjustmentRow } from '@/tariffs';
import { BillPdfDownloadButton } from '@/components/dashboard/BillPdfDocument';
import type { BillPdfData } from '@/components/dashboard/BillPdfDocument';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/cn';
import {
  IndianRupee, TrendingDown, Activity,
  AlertTriangle, CalendarDays, Target,
  Gauge, Clock, ShieldCheck, Info, Calendar,
} from 'lucide-react';
import { startOfMonth, endOfMonth, subMonths, format, getDaysInMonth, getDate, getMonth, getYear } from 'date-fns';


interface BillingProps { data: UseSheetData }

// ─── PDF section wrapper ───────────────────────────────────────────────────────

// ─── Previous-month data availability check ────────────────────────────────────
// Returns the number of distinct calendar days with data in a given month.
function countDaysInMonth(rows: import('@/types').TelemetryRow[], year: number, month: number): number {
  const days = new Set<string>();
  for (const r of rows) {
    if (r.timestamp.getFullYear() === year && r.timestamp.getMonth() === month) {
      days.add(format(r.timestamp, 'yyyy-MM-dd'));
    }
  }
  return days.size;
}

// ─── Bill Download Dropdown ────────────────────────────────────────────────────

type BillPeriod = 'current' | 'previous';

function BillDownloadDropdown({
  currentLabel,
  currentData,
  prevLabel,
  prevData,
  prevDayCount,
  prevDaysInMonth,
  minDays,
}: {
  currentLabel: string;
  currentData:  BillPdfData & { discLabel: string };
  prevLabel:    string;
  prevData:     (BillPdfData & { discLabel: string; cardLabel: string; cardSub: string }) | null;
  prevDayCount: number;
  prevDaysInMonth: number;
  minDays:      number;
}) {
  const [selected, setSelected] = useState<BillPeriod>('current');
  const hasPrev = prevData !== null;

  const activePdfData = selected === 'previous' && hasPrev ? prevData! : currentData;
  const activeLabel   = selected === 'previous' ? prevLabel : currentLabel;

  return (
    <Card padded>
      {/* ── Period selector ── */}
      <p className="text-xs font-semibold text-ink-700 mb-3">Select billing period</p>
      <div className="flex rounded-xl bg-ink-100 p-0.5 gap-0.5 mb-4">
        {/* Current month option — always available */}
        <button
          type="button"
          onClick={() => setSelected('current')}
          className={cn(
            'flex-1 py-2 px-3 rounded-lg text-[12px] font-semibold transition-all duration-150',
            selected === 'current'
              ? 'bg-white text-ink-900 shadow-sm'
              : 'text-ink-500 hover:text-ink-700',
          )}
        >
          {currentLabel}
        </button>

        {/* Previous month option — active only when ≥25 days */}
        <button
          type="button"
          onClick={() => hasPrev && setSelected('previous')}
          disabled={!hasPrev}
          className={cn(
            'flex-1 py-2 px-3 rounded-lg text-[12px] font-semibold transition-all duration-150',
            !hasPrev
              ? 'text-ink-300 cursor-not-allowed'
              : selected === 'previous'
                ? 'bg-white text-ink-900 shadow-sm'
                : 'text-ink-500 hover:text-ink-700',
          )}
        >
          {prevLabel}

        </button>
      </div>

      {/* ── Description line ── */}
      <p className="text-[10px] text-ink-400 mb-3 leading-relaxed">
        {selected === 'previous' && hasPrev
          ? `Full ${prevDaysInMonth}-day bill for ${prevLabel} · ${prevDayCount} days of meter data · all charges finalised.`
          : `Month-to-date bill for ${currentLabel} — energy charges, Power Factor & Load Factor adjustments, demand charge, duties, and projected net payable.`}
      </p>

      {/* ── Insufficient data notice (only when previous selected but unavailable) ── */}
      {selected === 'previous' && !hasPrev && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span className="text-[11px] text-amber-700 font-semibold">
            Insufficient data — {prevDayCount} of {minDays} days required for {prevLabel}.
            Check back when more data is synced.
          </span>
        </div>
      )}

      {/* ── Download button — disabled when previous is selected but unavailable ── */}
      {selected === 'previous' && !hasPrev ? (
        <button
          type="button"
          disabled
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm border-2 bg-ink-100 text-ink-400 border-ink-200 cursor-not-allowed"
        >
          Download Bill PDF — {activeLabel}
        </button>
      ) : (
        <BillPdfDownloadButton data={{ ...activePdfData, billingMonth: activeLabel }} />
      )}
    </Card>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────────────

const rs = (v: number | undefined) => formatRupees(v ?? 0);
const n2 = (v: number | undefined, d = 2) => formatNumber(v ?? 0, d);
const finite = (v: number | null | undefined) => (Number.isFinite(v) ? Number(v) : 0);

function getNiceAxisScale(minValue: number, maxValue: number): { min: number; max: number; ticks: number[] } {
  const safeMin = Math.max(0, Math.min(minValue, maxValue));
  const safeMax = Math.max(safeMin, maxValue);

  if (!Number.isFinite(safeMin) || !Number.isFinite(safeMax) || safeMax === safeMin) {
    const upper = Math.max(1, safeMax || 0);
    return { min: 0, max: upper, ticks: [0, upper] };
  }

  const span = safeMax - safeMin;
  const stepCandidates = [50, 100, 250, 500, 1000, 2500, 5000, 10000];
  const desiredStep = span / 4;
  const step = stepCandidates.find((candidate) => candidate >= desiredStep) ?? stepCandidates[stepCandidates.length - 1];

  let min = Math.floor(safeMin / step) * step;
  let max = Math.ceil(safeMax / step) * step;

  if (max <= min) {
    max = min + step * 4;
  }

  const ticks: number[] = [];
  for (let value = min; value <= max + step / 2; value += step) {
    ticks.push(value);
  }

  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return { min, max, ticks };
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-1 pt-2 pb-1">
      <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-ink-400">{children}</span>
      <div className="flex-1 h-px bg-ink-100" />
    </div>
  );
}

function KpiCard({
  label, value, sub, icon: Icon, highlight, className,
}: {
  label: string; value: string; sub?: string;
  icon?: React.ElementType; highlight?: 'good' | 'danger' | 'warn' | 'neutral';
  className?: string;
}) {
  const iconColor = highlight === 'good' ? 'text-emerald-500'
    : highlight === 'danger' ? 'text-red-500'
    : highlight === 'warn' ? 'text-amber-500'
    : 'text-brand-500';
  const valueCls = highlight === 'good' ? 'text-emerald-600'
    : highlight === 'danger' ? 'text-red-600'
    : highlight === 'warn' ? 'text-amber-600'
    : 'text-ink-900';

  return (
    <Card padded className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
        {Icon && <Icon className={cn('w-4 h-4', iconColor)} />}
      </div>
      <span className={cn('num font-bold text-xl leading-tight', valueCls)}>{value}</span>
      {sub && <span className="text-[10px] text-ink-400 leading-snug">{sub}</span>}
    </Card>
  );
}

function BillRow({
  label, value, sub, indent = false, bold = false, savings = false, penalty = false,
}: {
  label: string; value: string; sub?: string;
  indent?: boolean; bold?: boolean; savings?: boolean; penalty?: boolean;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-2 py-1.5', indent && 'pl-4')}>
      <div className="min-w-0">
        <span className={cn('text-xs text-ink-700', bold && 'font-semibold text-ink-900')}>{label}</span>
        {sub && <span className="text-[10px] text-ink-400 ml-1">({sub})</span>}
      </div>
      <span className={cn(
        'num text-xs font-semibold shrink-0',
        savings ? 'text-emerald-600' : penalty ? 'text-red-600' : 'text-ink-900',
        bold && 'text-sm',
      )}>
        {value}
      </span>
    </div>
  );
}

// ─── Custom Recharts tooltip ───────────────────────────────────────────────────
function RsTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-ink-100 rounded-xl shadow-cardLg px-3 py-2 text-xs">
      <p className="font-semibold text-ink-900 mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-ink-600">{p.name}:</span>
          <span className="font-semibold">{formatRupees(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── PF slab table row ─────────────────────────────────────────────────────────

function PfTableRow({
  row,
  activeNormal = false,
  activePeak = false,
  activeOffpeak = false,
  activeRow = false,
}: {
  row: PfAdjustmentRow;
  activeNormal?: boolean;
  activePeak?: boolean;
  activeOffpeak?: boolean;
  activeRow?: boolean;
}) {
  const pfLabel = row.maxPf === Infinity
    ? `≥ ${row.minPf.toFixed(2)}`
    : `${row.minPf.toFixed(2)} – ${row.maxPf.toFixed(2)}`;
  const anyCell  = activeNormal || activePeak || activeOffpeak;
  const rowGlow  = activeRow || anyCell;
  const cellCls = (pct: number, active: boolean) => cn(
    'text-center rounded-md py-0.5 transition-colors',
    pct > 0 ? 'text-emerald-600' : pct < 0 ? 'text-red-600' : 'text-ink-400',
    active && 'font-bold ring-1 ring-brand-300 bg-brand-50',
  );
  return (
    <div className={cn(
      'grid grid-cols-4 gap-1 text-[10px] py-1.5 px-2 rounded-lg',
      rowGlow
        ? (activeRow && !anyCell ? 'bg-brand-50 ring-1 ring-brand-200' : 'bg-brand-50/40')
        : 'odd:bg-ink-50/50',
    )}>
      <span className={cn('font-mono', rowGlow && 'font-bold text-brand-700')}>{pfLabel}</span>
      <span className={cellCls(row.normalPct, activeNormal)}>
        {row.normalPct > 0 ? '+' : ''}{row.normalPct}%
      </span>
      <span className={cellCls(row.peakPct, activePeak)}>
        {row.peakPct > 0 ? '+' : ''}{row.peakPct}%
      </span>
      <span className={cellCls(row.offPeakPct, activeOffpeak)}>
        {row.offPeakPct > 0 ? '+' : ''}{row.offPeakPct}%
      </span>
    </div>
  );
}

// ─── Per-period PF gauge (one bar each for Normal / Peak / Off-Peak) ──────────

function PfPeriodGauge({
  label, pf, adjPct, color,
}: { label: string; pf: number; adjPct: number; color: string }) {
  const pfColor = pf >= 0.99 ? 'text-emerald-600'
    : pf > 0 && pf < 0.80 ? 'text-red-600'
    : pf >= 0.95 ? 'text-brand-600'
    : 'text-ink-900';
  const adjColor = adjPct > 0 ? 'text-emerald-600' : adjPct < 0 ? 'text-red-600' : 'text-ink-500';
  const adjLabel = adjPct > 0 ? 'Rebate' : adjPct < 0 ? 'Penalty' : 'No adj.';
  return (
    <div className="p-2.5 rounded-xl bg-ink-50 flex items-center gap-3">
      <div className="flex flex-col items-center w-16 shrink-0">
        <span className={cn('num text-lg font-bold leading-none', pfColor)}>
          {pf > 0 ? formatPF(pf) : '—'}
        </span>
        <span className="text-[9px] font-semibold uppercase tracking-wide mt-1" style={{ color }}>
          {label}
        </span>
      </div>
      <div className="flex-1 h-3 bg-ink-200 rounded-full overflow-hidden relative">
        <div className="absolute inset-0 flex">
          <div className="h-full bg-red-300"     style={{ width: '80%' }} />
          <div className="h-full bg-amber-200"   style={{ width: '15%' }} />
          <div className="h-full bg-emerald-300" style={{ width: '5%' }} />
        </div>
        {pf > 0 && (
          <div
            className="absolute top-0 w-1 h-full bg-ink-900 rounded-full shadow-md"
            style={{ left: `${Math.min(99, Math.max(0, pf * 100))}%` }}
          />
        )}
      </div>
      <div className="flex flex-col items-end w-12 shrink-0">
        <span className={cn('num text-sm font-bold leading-none', adjColor)}>
          {adjPct > 0 ? '+' : ''}{adjPct}%
        </span>
        <span className="text-[9px] text-ink-400 uppercase tracking-wide mt-1">{adjLabel}</span>
      </div>
    </div>
  );
}

// ─── Per-period PF rebate/surcharge box (footer trio) ─────────────────────────

function PfPeriodBox({
  label, amount, color, rs,
}: { label: string; amount: number; color: string; rs: (v: number) => string }) {
  // amount > 0 → rebate (deducted from bill); amount < 0 → surcharge (added).
  return (
    <div className={cn(
      'rounded-xl p-2.5 flex flex-col gap-0.5 ring-1',
      amount > 0 ? 'bg-emerald-50/60 ring-emerald-200'
      : amount < 0 ? 'bg-red-50/60 ring-red-200'
      : 'bg-ink-50 ring-ink-100',
    )}>
      <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color }}>
        {label}
      </span>
      <span className={cn(
        'num text-sm font-bold',
        amount > 0 ? 'text-emerald-600' : amount < 0 ? 'text-red-600' : 'text-ink-500',
      )}>
        {amount > 0 ? '−' : amount < 0 ? '+' : ''}{rs(Math.abs(amount))}
      </span>
      <span className="text-[9px] text-ink-400">
        {amount > 0 ? 'rebate' : amount < 0 ? 'surcharge' : 'no adj.'}
      </span>
    </div>
  );
}

// ─── LF rebate / surcharge bands (WBSEDCL Tariff Order 2025-26) ─────────────
// All three WB factories (Taparia 0.4 kV, Omega 11 kV, JD Powercorn 11 kV)
// are "Below 33 kV" consumers. Rates below are the "Below 33 kV" column.
// Negative rateRsKwh = surcharge (added to bill).
// Positive rateRsKwh = rebate (deducted from bill).

const LF_REBATE_BANDS = [
  // ── Surcharge bands ───────────────────────────────────────────────────────
  { label: '0% ≤ LF < 25%',   minLf:  0, maxLf:  25, rateRsKwh: -0.35, surcharge: true,  rebate: false },
  { label: '25% ≤ LF < 30%',  minLf: 25, maxLf:  30, rateRsKwh: -0.15, surcharge: true,  rebate: false },
  { label: '30% ≤ LF < 60%',  minLf: 30, maxLf:  60, rateRsKwh: -0.14, surcharge: true,  rebate: false },
  { label: '60% ≤ LF < 65%',  minLf: 60, maxLf:  65, rateRsKwh: -0.07, surcharge: true,  rebate: false },
  // ── No adjustment dead-band ───────────────────────────────────────────────
  { label: '65% ≤ LF < 70%',  minLf: 65, maxLf:  70, rateRsKwh:  0.00, surcharge: false, rebate: false },
  // ── Rebate bands ──────────────────────────────────────────────────────────
  { label: '70% ≤ LF < 75%',  minLf: 70, maxLf:  75, rateRsKwh:  0.06, surcharge: false, rebate: true  },
  { label: '75% ≤ LF < 80%',  minLf: 75, maxLf:  80, rateRsKwh:  0.11, surcharge: false, rebate: true  },
  { label: '80% ≤ LF < 85%',  minLf: 80, maxLf:  85, rateRsKwh:  0.16, surcharge: false, rebate: true  },
  { label: '85% ≤ LF < 90%',  minLf: 85, maxLf:  90, rateRsKwh:  0.21, surcharge: false, rebate: true  },
  { label: '90% ≤ LF < 92%',  minLf: 90, maxLf:  92, rateRsKwh:  0.26, surcharge: false, rebate: true  },
  { label: '92% ≤ LF < 95%',  minLf: 92, maxLf:  95, rateRsKwh:  0.31, surcharge: false, rebate: true  },
  { label: 'LF ≥ 95%',        minLf: 95, maxLf: 101, rateRsKwh:  0.36, surcharge: false, rebate: true  },
] as const;

function LfTableRow({ band, active }: { band: typeof LF_REBATE_BANDS[number]; active: boolean }) {
  const isSurcharge = band.surcharge;
  return (
    <div className={cn(
      'grid grid-cols-2 gap-1 text-[10px] py-1.5 px-2 rounded-lg',
      active ? 'bg-brand-50 ring-1 ring-brand-200' : 'odd:bg-ink-50/50',
    )}>
      <span className={cn('font-mono', active && 'font-bold text-brand-700')}>
        {band.label}
        {active && (
          <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-brand-100 text-brand-700">
            current
          </span>
        )}
      </span>
      <span className={cn(
        'text-center',
        isSurcharge
          ? (active ? 'text-red-600 font-bold' : 'text-red-500')
          : band.rebate
            ? (active ? 'text-emerald-600 font-bold' : 'text-emerald-600')
            : 'text-ink-400',
      )}>
        {isSurcharge
          ? `+₹${Math.abs(band.rateRsKwh).toFixed(2)} surcharge / kWh`
          : band.rebate
            ? `₹${band.rateRsKwh.toFixed(2)} rebate / kWh`
            : 'No adjustment'}
      </span>
    </div>
  );
}

// ─── ToD period toggle ─────────────────────────────────────────────────────────

type TodViewPeriod = 'today' | 'month' | 'custom';

function TodToggle({
  active, onChange, customFrom, customTo, onCustomChange, dataEndDate,
}: {
  active: TodViewPeriod;
  onChange: (p: TodViewPeriod) => void;
  customFrom: string;
  customTo: string;
  onCustomChange: (from: string, to: string) => void;
  dataEndDate: Date;
}) {
  const today = dataEndDate;
  const minDate = new Date(today);
  minDate.setMonth(minDate.getMonth() - 3);
  // Use local date parts to avoid UTC offset issues in IST
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const minStr = toLocalDateStr(minDate);
  const maxStr = toLocalDateStr(today);

  return (
    <div className="mt-2 mb-3">
      <div className="flex rounded-lg bg-ink-100 p-0.5 gap-0.5">
        {(['today', 'month', 'custom'] as TodViewPeriod[]).map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={cn(
              'flex-1 py-1 text-[11px] font-semibold rounded-md transition-colors',
              active === p ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700',
            )}
          >
            {p === 'today' ? 'Today' : p === 'month' ? 'This Month' : 'Custom'}
          </button>
        ))}
      </div>
      {active === 'custom' && (
        <div className="mt-2 flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-ink-400 shrink-0" />
          <input
            type="date"
            value={customFrom}
            min={minStr}
            max={customTo || maxStr}
            onChange={(e) => onCustomChange(e.target.value, customTo)}
            className="flex-1 text-[11px] text-ink-700 bg-ink-50 border border-ink-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
          <span className="text-[11px] text-ink-400">to</span>
          <input
            type="date"
            value={customTo}
            min={customFrom || minStr}
            max={maxStr}
            onChange={(e) => onCustomChange(customFrom, e.target.value)}
            className="flex-1 text-[11px] text-ink-700 bg-ink-50 border border-ink-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function BillingPage({ data }: BillingProps) {
  const { rows, status, error } = data;
  const { selectedSheet } = useAuth();

  // Active state tariff — drives all display labels, rates, windows & charges.
  const tariff = useTariff();
  // Local aliases (shadow the legacy hardcoded config imports) so the entire
  // page renders with the active state's values (WBSEDCL or TGSPDCL).
  const TOD_WINDOWS = tariff.todWindows;
  const TOD_COLORS  = tariff.todColors;
  const TARIFF_RATES = tariff.energyRates;
  const pfAdjustmentPct = tariff.pfAdjustmentPct;
  const PF_ADJUSTMENT_TABLE = tariff.pfAdjustmentTable;
  const DEMAND_CHARGE_RS_PER_KVA = tariff.demandChargeRsKva;
  const DEMAND_PENAL_RS_PER_KVA  = tariff.demandPenalRsKva;
  const METER_RENTAL_RS_MONTH = tariff.meterRentalRsMonth;
  const TPR_PCT = tariff.tprFraction * 100;
  const energyUnitLabel = tariff.energyUnit === 'kvah' ? 'kVAh' : 'kWh';

  // Use per-sheet contract demand if configured, fall back to the tariff default.
  const contractDemandKva = selectedSheet?.meta?.contractDemandKva ?? tariff.defaultContractDemandKva;
  const minChargeableKva  = contractDemandKva * tariff.minDemandPct; // min chargeable demand

  if (status === 'loading' && !rows.length) return <PageLoadingSkeleton />;
  if (status === 'error'   && !rows.length) return <PageErrorState message={error} onRetry={() => void data.refetch()} />;

  // Latest row carries the running accumulators (month-to-date)
  const latest = rows.length ? rows[rows.length - 1] : null;
  // Data-driven "now" — use the last row's timestamp, not the wall clock
  const dataEnd = latest?.timestamp ?? new Date();
  const today = format(dataEnd, 'yyyy-MM-dd');
  const todayRows = useMemo(() => rows.filter(r => format(r.timestamp, 'yyyy-MM-dd') === today), [rows, today]);
  const monthStartStr = format(startOfMonth(dataEnd), 'yyyy-MM-dd');
  const billingRows = useMemo(
    () => rows.filter(r => format(r.timestamp, 'yyyy-MM-dd') >= monthStartStr),
    [rows, monthStartStr],
  );

  // ── Previous-month billing data ───────────────────────────────────────────
  // Compute a fully-validated breakdown for the calendar month immediately
  // preceding the current billing month. Only shown when ≥25 distinct days
  // have meter data (validates data completeness before generating a bill).
  const prevMonthDate    = subMonths(startOfMonth(dataEnd), 1);
  const prevMonthYear    = getYear(prevMonthDate);
  const prevMonthMonth   = getMonth(prevMonthDate);   // 0-indexed
  const prevMonthStart   = format(prevMonthDate, 'yyyy-MM-dd');
  const prevMonthEnd     = format(endOfMonth(prevMonthDate), 'yyyy-MM-dd');
  const prevMonthLabel   = format(prevMonthDate, 'MMMM yyyy').toUpperCase();

  const prevMonthRows = useMemo(
    () => rows.filter(r => {
      const d = format(r.timestamp, 'yyyy-MM-dd');
      return d >= prevMonthStart && d <= prevMonthEnd;
    }),
    [rows, prevMonthStart, prevMonthEnd],
  );

  const prevMonthDayCount = useMemo(
    () => countDaysInMonth(prevMonthRows, prevMonthYear, prevMonthMonth),
    [prevMonthRows, prevMonthYear, prevMonthMonth],
  );
  const PREV_MONTH_MIN_DAYS = 25;
  const prevMonthSufficient = prevMonthDayCount >= PREV_MONTH_MIN_DAYS;

  // Full billing breakdown for the previous month (runs only when sufficient data).
  const prevDaysInMonth  = getDaysInMonth(prevMonthDate);
  const prevBreakdown    = useMemo(
    () => prevMonthSufficient
      ? buildBillBreakdown(prevMonthRows, prevDaysInMonth, prevDaysInMonth, contractDemandKva, tariff, selectedSheet?.meta?.acdSurchargeRs ?? 0)
      : null,
    [prevMonthRows, prevDaysInMonth, contractDemandKva, tariff, prevMonthSufficient],
  );

  // Previous month: demand derived from the breakdown's peakKva
  // (maxDemandKvaOf on the full month's rows for the billing-cycle peak).
  const prevMaxDemandKva = useMemo(
    () => prevMonthSufficient && prevMonthRows.length >= 2
      ? maxDemandKvaOf(prevMonthRows)
      : 0,
    [prevMonthRows, prevMonthSufficient],
  );

  // Assemble all scalars needed to populate BillPdfData for the previous month.
  // Mirrors exactly the logic in the current-month PDF prop block below.
  const prevPdfData = useMemo((): (BillPdfData & { discLabel: string; cardLabel: string; cardSub: string }) | null => {
    if (!prevBreakdown || !prevMonthSufficient) return null;
    const bk = prevBreakdown;

    const prevLatest = prevMonthRows[prevMonthRows.length - 1];
    const prevFirst  = prevMonthRows[0];

    // Energy charges
    const prevEcNormal   = bk.periods.find(p => p.period === 'NORMAL')?.energyChargeRs   ?? 0;
    const prevEcPeak     = bk.periods.find(p => p.period === 'PEAK')?.energyChargeRs     ?? 0;
    const prevEcOffpeak  = bk.periods.find(p => p.period === 'OFF-PEAK')?.energyChargeRs ?? 0;
    const prevEcTotal    = bk.ecTotalRs;

    // PF
    const prevPfAvg         = bk.pfWeighted;
    const prevPfRebatePct   = tariff.pfAdjustmentPct(prevPfAvg, 'NORMAL');
    const prevPfRebateCost  = -(bk.pfAdjTotalRs);
    const prevPfPctNormal   = bk.periods.find(p => p.period === 'NORMAL')?.pfAdjPct   ?? 0;
    const prevPfPctPeak     = bk.periods.find(p => p.period === 'PEAK')?.pfAdjPct     ?? 0;
    const prevPfPctOffpeak  = bk.periods.find(p => p.period === 'OFF-PEAK')?.pfAdjPct ?? 0;

    // LF
    const prevLfPct        = bk.lfPct ?? 0;
    const prevLfRebateCost = -(bk.lfRebateRs);

    // Demand (full month — no proration)
    // BOTH states: use billing engine breakdown (bk) as single source of truth,
    // matching the current-month logic which uses breakdown.dcNormalRs / dcPenalRs / dcRs.
    const prevChargeableKva   = tariff.energyUnit === 'kvah'
      ? bk.normalKva                                                          // TGSPDCL: normalKva = AP
      : bk.chargeableKva;                                                     // WB: chargeableKva = BI
    const prevAddlKva         = bk.addlKva;                                   // BJ (WB) or AQ (TG)
    const prevDemandChargeRs  = bk.dcNormalRs;                               // BK (WB Normal DC) or AR (TG)
    const prevMdOverrunPenalty= bk.dcPenalRs;                                // BL (WB Addl DC) or AS (TG)
    const prevTotalDemandRs   = bk.dcRs;                                     // BM = BK+BL (WB) or AT (TG)

    // Net EC (energy side)
    const prevTotalCostMtd = tariff.energyUnit === 'kvah'
      ? bk.ecTotalRs
      : (bk.ecTotalRs + bk.pfAdjTotalRs + bk.lfRebateRs);

    // Bill assembly (mirrors current-month logic exactly)
    // For TGSPDCL (kVAh): use bk.subTotalRs (EC + DC + ED) and bk.grossPayableRs (Sub Total + Customer)
    //   directly from the billing engine — these are already the canonical correct values.
    // For WB (kWh): keep existing manual formula unchanged.
    const prevGrossBeforeEd = prevTotalCostMtd + prevTotalDemandRs;
    const prevEdRs = bk.electricityDutyRs || (
      tariff.electricityDutyModel === 'pct-of-bill'
        ? Math.max(0, prevGrossBeforeEd) * tariff.electricityDutyValue
        : bk.totalKvah * tariff.electricityDutyValue
    );
    const prevMeterRental    = bk.meterRentalRs    || tariff.meterRentalRsMonth;
    const prevCustomerCharge = bk.customerChargesRs || 0;
    // TGSPDCL: use engine values (sub total already includes ED; gross = sub + customer only)
    // WB: use manual assembly (gross = energyAndDemand + ED + meterRental + customer)
    const prevSubTotalRs   = tariff.energyUnit === 'kvah'
      ? bk.subTotalRs                                                          // TGSPDCL: from engine
      : prevGrossBeforeEd;                                                     // WB: before ED
    const prevGrossPayable = tariff.energyUnit === 'kvah'
      ? bk.grossPayableRs                                                      // TGSPDCL: Sub Total + Customer
      : prevGrossBeforeEd + prevEdRs + prevMeterRental + prevCustomerCharge;   // WB: unchanged
    const prevTprRs          = prevGrossPayable * tariff.tprFraction;
    const prevNetPayable     = prevGrossPayable - prevTprRs;

    // kWh for PDF meter reading section
    const prevKwhNormal  = prevEcNormal  > 0 ? prevEcNormal  / tariff.energyRates.NORMAL          : 0;
    const prevKwhPeak    = prevEcPeak    > 0 ? prevEcPeak    / tariff.energyRates.PEAK             : 0;
    const prevKwhOffpeak = prevEcOffpeak > 0 ? prevEcOffpeak / tariff.energyRates['OFF-PEAK']      : 0;

    // For the previous month bill, daysPassed = daysTotal (full month, no projection)
    return {
      stateCode:          tariff.stateCode,
      discLabel:          `${tariff.discName} ${tariff.tariffCategory}`,
      cardLabel:          `Previous Month Bill · ${prevMonthLabel}`,
      cardSub:            `Full ${prevDaysInMonth}-day bill for ${prevMonthLabel} · ${prevMonthDayCount} days of meter data · all charges finalised.`,
      billingMonth:       prevMonthLabel,
      billingDate:        prevMonthEnd,
      readingFromDate:    format(startOfMonth(prevMonthDate), 'dd-MMM-yy'), // opening (01-<prev>-26)
      readingToDate:      format(endOfMonth(prevMonthDate),   'dd-MMM-yy'), // closing (last-<prev>-26)
      invoiceNo:          `PREV-${format(prevMonthDate, 'yyyyMM')}-${selectedSheet?.meta?.consumerId ?? '000'}`,
      consumerName:       selectedSheet?.label ?? 'Consumer',
      consumerId:         selectedSheet?.meta?.consumerId ?? '—',
      installationNo:     selectedSheet?.id ?? '—',
      serviceAddress:     selectedSheet?.meta?.location ?? selectedSheet?.label ?? '—',
      meterNo:            prevLatest?.meterSerial ?? '—',
      tariffCode:         selectedSheet?.meta?.tariffCode ?? tariff.tariffCategory,
      supplyVoltageKv:    tariff.voltageLevel.replace(/[^0-9.]/g, '') || '0.40',
      contractDemandKva,
      panNo:              '—',
      feeder:             selectedSheet?.meta?.feeder ?? '—',
      category:           selectedSheet?.meta?.category ?? tariff.tariffCategory,
      payableDate:        prevMonthEnd,
      disconnectionDate:  prevMonthEnd,
      readingCurrentKwh:  prevLatest?.energyKwh ?? 0,
      readingPreviousKwh: prevFirst?.energyKwh ?? 0,
      readingCurrentKvah: prevLatest?.energyVAh ?? 0,
      readingPreviousKvah:prevFirst?.energyVAh ?? 0,
      differenceKwh:      (prevLatest?.energyKwh ?? 0) - (prevFirst?.energyKwh ?? 0),
      differenceKvah:     (prevLatest?.energyVAh ?? 0) - (prevFirst?.energyVAh ?? 0),
      multiplyingFactor:  1,
      consumptionKwh:     bk.totalKvah,
      consumptionKvah:    bk.totalKvah,
      consumptionKva:     bk.peakKva,
      tod1Kvah:           tariff.energyUnit === 'kvah' ? (bk.todPeakKvah ?? 0)    : prevKwhPeak,
      tod2Kvah:           tariff.energyUnit === 'kvah' ? (bk.todOffpeakKvah ?? 0) : prevKwhOffpeak,
      rateNormal:         tariff.energyRates.NORMAL,
      ratePeak:           tariff.energyRates.PEAK,
      rateOffpeak:        tariff.energyRates['OFF-PEAK'],
      todChargeRs:        tariff.energyUnit === 'kvah'
        ? ((bk.todPeakPenaltyRs ?? 0) - (bk.todOffpeakRebateRs ?? 0))
        : 0,
      todKvah:            bk.totalKvah,
      // Telangana ToD breakdown fields for previous month
      bkEcBaseRs:         tariff.energyUnit === 'kvah' ? (bk.ecBaseRs ?? prevEcTotal) : undefined,
      bkTodPeakKvah:      tariff.energyUnit === 'kvah' ? (bk.todPeakKvah ?? 0)    : undefined,
      bkTodOffpeakKvah:   tariff.energyUnit === 'kvah' ? (bk.todOffpeakKvah ?? 0) : undefined,
      bkTodPeakPenaltyRs: tariff.energyUnit === 'kvah' ? (bk.todPeakPenaltyRs ?? 0)   : undefined,
      bkTodOffpeakRebateRs: tariff.energyUnit === 'kvah' ? (bk.todOffpeakRebateRs ?? 0): undefined,
      energyChargeNormal: prevEcNormal,
      energyChargePeak:   prevEcPeak,
      energyChargeOffpeak:prevEcOffpeak,
      totalEnergyCharge:  prevEcTotal,
      kwhNormal:          prevKwhNormal,
      kwhPeak:            prevKwhPeak,
      kwhOffpeak:         prevKwhOffpeak,
      pfAvg:              prevPfAvg,
      pfRebatePct:        prevPfRebatePct,
      pfRebatePctNormal:  prevPfPctNormal,
      pfRebatePctPeak:    prevPfPctPeak,
      pfRebatePctOffpeak: prevPfPctOffpeak,
      pfRebateCost:       prevPfRebateCost,
      lfPct:              prevLfPct,
      lfRebateCost:       prevLfRebateCost,
      maxDemandKva:       prevMaxDemandKva,
      normalKva:          prevChargeableKva,
      addlKva:            prevAddlKva,
      // PDF BE/BF/BG: per-period Max Demand kVA (power, not energy)
      // Previous code incorrectly used billing period kvah (energy) here.
      maxDemandKvaNormal:  bk.mdKvaNormal  ?? 0,
      maxDemandKvaPeak:    bk.mdKvaPeak    ?? 0,
      maxDemandKvaOffpeak: bk.mdKvaOffpeak ?? 0,
      chargeableKva:      prevChargeableKva,
      demandChargeRs:     prevDemandChargeRs,
      mdOverrunKva:       prevAddlKva,
      mdOverrunPenaltyRs: prevMdOverrunPenalty,
      grossBeforeEd:      prevGrossBeforeEd,
      subTotalRs:         prevSubTotalRs,
      electricityDutyRs:  prevEdRs,
      colonyChargesRs:    0,
      lfChargesRs:        0,
      customerChargesRs:  prevCustomerCharge,
      acdSurchargeRs:     bk.acdSurchargeRs,
      uiChargesRs:        0,
      crossSubsidyRs:     0,
      addlSurchargeRs:    0,
      latePaymentRs:      0,
      interestOnEdRs:     0,
      incTod1Rs:          0,
      incTod2Rs:          0,
      wheelingChargesRs:  0,
      transmissionRs:     0,
      gridSupportRs:      0,
      otherCharges1Rs:    0,
      otherCharges2Rs:    0,
      grossTotalRs:       prevGrossPayable,
      otherCreditAdjRs:   0,
      meterRentalRs:      prevMeterRental,
      grossPayable:       prevGrossPayable,
      grossPayableRs:     prevGrossPayable,
      tprRs:              prevTprRs,
      netPayable:         prevNetPayable,
      projectedNetPayable:prevNetPayable,   // full month — actual, not projected
      daysPassed:         prevDaysInMonth,
      daysInMonth:        prevDaysInMonth,
      peakDemandTimestamp:undefined,
      totalAmountPayableRs:prevNetPayable,
      totalArrearsRs:     0,
      netBillAmountRs:    prevNetPayable,
      // No projection fields — previous month is complete
      projectedGrossEcRs: prevEcTotal,
      projectedPfAdjRs:   prevPfRebateCost > 0 ? prevPfRebateCost : 0,
      projectedLfAdjRs:   prevLfRebateCost > 0 ? prevLfRebateCost : 0,
      projectedEdRs:      prevEdRs,
      projectedTprRs:     prevTprRs,
      projectedKvahNormal:  bk.projectedKvah?.['NORMAL']   ?? prevKwhNormal,
      projectedKvahPeak:    bk.projectedKvah?.['PEAK']     ?? prevKwhPeak,
      projectedKvahOffpeak: bk.projectedKvah?.['OFF-PEAK'] ?? prevKwhOffpeak,

      // ── West Bengal (WBSEDCL) specific fields ───────────────────────────
      // Telangana renderer ignores all wbXxx fields.
      ...(tariff.energyUnit !== 'kvah' ? {
        // Previous Reading Date = first day of the previous billing month
        wbPreviousReadingDate: format(prevMonthDate, 'dd-MMM-yyyy'),

        // Per-period average PF from billing engine — drives per-period PF slabs
        wbPfAvgNormal:  bk.periods.find(p => p.period === 'NORMAL')?.pfAvg   ?? bk.pfWeighted,
        wbPfAvgPeak:    bk.periods.find(p => p.period === 'PEAK')?.pfAvg     ?? bk.pfWeighted,
        wbPfAvgOffpeak: bk.periods.find(p => p.period === 'OFF-PEAK')?.pfAvg ?? bk.pfWeighted,

        // Per-period MTD kWh from billing engine — "This Month" dashboard values
        wbKwhNormal:  bk.periods.find(p => p.period === 'NORMAL')?.kwh   ?? 0,
        wbKwhPeak:    bk.periods.find(p => p.period === 'PEAK')?.kwh     ?? 0,
        wbKwhOffpeak: bk.periods.find(p => p.period === 'OFF-PEAK')?.kwh ?? 0,

        // Per-period kWh at month start — derive from first row of previous month
        // Same methodology as current month: Present − Previous = MTD kWh
        wbPrevKwhNormal: (() => {
          const prevKwhN    = bk.periods.find(p => p.period === 'NORMAL')?.kwh   ?? 0;
          const prevKwhPk   = bk.periods.find(p => p.period === 'PEAK')?.kwh     ?? 0;
          const prevKwhOff  = bk.periods.find(p => p.period === 'OFF-PEAK')?.kwh ?? 0;
          const total       = prevKwhN + prevKwhPk + prevKwhOff;
          if (total <= 0) return 0;
          const currentKwh  = prevLatest?.energyKwh ?? 0;
          return Math.max(0, currentKwh * (prevKwhN / total) - prevKwhN);
        })(),
        wbPrevKwhPeak: (() => {
          const prevKwhN    = bk.periods.find(p => p.period === 'NORMAL')?.kwh   ?? 0;
          const prevKwhPk   = bk.periods.find(p => p.period === 'PEAK')?.kwh     ?? 0;
          const prevKwhOff  = bk.periods.find(p => p.period === 'OFF-PEAK')?.kwh ?? 0;
          const total       = prevKwhN + prevKwhPk + prevKwhOff;
          if (total <= 0) return 0;
          const currentKwh  = prevLatest?.energyKwh ?? 0;
          return Math.max(0, currentKwh * (prevKwhPk / total) - prevKwhPk);
        })(),
        wbPrevKwhOffpeak: (() => {
          const prevKwhN    = bk.periods.find(p => p.period === 'NORMAL')?.kwh   ?? 0;
          const prevKwhPk   = bk.periods.find(p => p.period === 'PEAK')?.kwh     ?? 0;
          const prevKwhOff  = bk.periods.find(p => p.period === 'OFF-PEAK')?.kwh ?? 0;
          const total       = prevKwhN + prevKwhPk + prevKwhOff;
          if (total <= 0) return 0;
          const currentKwh  = prevLatest?.energyKwh ?? 0;
          return Math.max(0, currentKwh * (prevKwhOff / total) - prevKwhOff);
        })(),

        // Per-period max demand kVA from billing engine
        wbMdKvaNormal:  bk.mdKvaNormal,
        wbMdKvaPeak:    bk.mdKvaPeak,
        wbMdKvaOffpeak: bk.mdKvaOffpeak,
      } : {}),
    };
  }, [
    prevBreakdown, prevMonthSufficient, prevMonthRows, prevMaxDemandKva,
    contractDemandKva, tariff, prevDaysInMonth, prevMonthDayCount,
    prevMonthLabel, prevMonthEnd, selectedSheet,
  ]);

  // ── Month projection constants ───────────────────────────────────────────
  const daysPassed  = getDate(dataEnd);
  const daysInMonth = getDaysInMonth(dataEnd);
  const daysRemaining = daysInMonth - daysPassed;

  // ── Peak demand row — find the 15-min block with the highest avg kVA,
  // then pick the billing row closest to that block's end timestamp.
  // This is reliable for WB (where maxKva Pi accumulator is often 0/missing).
  const maxDemandRow = useMemo(() => {
    if (!billingRows.length) return null;
    const blocks = fifteenMinDemandBlocks(billingRows.length >= 2 ? billingRows : rows);
    if (!blocks.length) return null;
    const peakBlock = blocks.reduce((best, b) => b.kva > best.kva ? b : best, blocks[0]);
    if (peakBlock.kva <= 0) return null;
    // Find the billing row whose timestamp is closest to the end of the peak block
    const peakBlockEnd = peakBlock.ts + 15 * 60 * 1000;
    return billingRows.reduce((closest, r) => {
      const diff = Math.abs(r.timestamp.getTime() - peakBlockEnd);
      const bestDiff = Math.abs(closest.timestamp.getTime() - peakBlockEnd);
      return diff < bestDiff ? r : closest;
    }) ?? null;
  }, [billingRows, rows]);

  // ── BillBreakdown — single source of truth, computed from raw rows ────────
  // Pi accumulator fields (latest?.totalCost etc.) are unreliable when the
  // DERIVED sheet doesn't populate them yet. buildBillBreakdown recomputes
  // everything from the raw telemetry so the page always shows real numbers.
  const breakdown = useMemo(
    () => buildBillBreakdown(billingRows, daysPassed, daysInMonth, contractDemandKva, tariff, selectedSheet?.meta?.acdSurchargeRs ?? 0),
    [billingRows, daysPassed, daysInMonth, contractDemandKva, tariff],
  );

  // MTD energy charges — prefer Pi accumulators (more accurate running total),
  // fall back to breakdown when they're 0/missing
  const piTotalEnergyCharge  = latest?.totalEnergyCharge  ?? 0;
  const piEcNormal           = latest?.energyChargeNormal  ?? 0;
  const piEcPeak             = latest?.energyChargePeak    ?? 0;
  const piEcOffpeak          = latest?.energyChargeOffpeak ?? 0;
  const piPfRebateCost       = latest?.pfRebateCost        ?? 0;
  const piLfRebateCost       = latest?.lfRebateCost        ?? 0;

  // Use breakdown values when Pi fields are 0 (not yet populated in DERIVED)
  const bkEcNormal   = breakdown.periods.find(p => p.period === 'NORMAL')?.energyChargeRs   ?? 0;
  const bkEcPeak     = breakdown.periods.find(p => p.period === 'PEAK')?.energyChargeRs     ?? 0;
  const bkEcOffpeak  = breakdown.periods.find(p => p.period === 'OFF-PEAK')?.energyChargeRs ?? 0;
  const bkKwhNormal  = breakdown.periods.find(p => p.period === 'NORMAL')?.kwh   ?? 0;
  const bkKwhPeak    = breakdown.periods.find(p => p.period === 'PEAK')?.kwh     ?? 0;
  const bkKwhOffpeak = breakdown.periods.find(p => p.period === 'OFF-PEAK')?.kwh ?? 0;

  // ── Telangana ToD breakdown (TGSPDCL model) ────────────────────────────────
  // These values come from billing.ts and represent the new billing structure:
  //   Base EC = totalKvah × Normal rate
  //   ToD Peak Penalty = peakKvah × (peakRate − normalRate)  [positive, adds to bill]
  //   ToD Off-Peak Rebate = offpeakKvah × (normalRate − offpeakRate) [positive = saving]
  const bkTodPeakKvah       = breakdown.todPeakKvah       ?? 0;
  const bkTodOffpeakKvah    = breakdown.todOffpeakKvah     ?? 0;
  const bkEcBaseRs          = breakdown.ecBaseRs           ?? breakdown.ecTotalRs;
  const bkTodPeakPenaltyRs  = breakdown.todPeakPenaltyRs   ?? 0;
  const bkTodOffpeakRebateRs= breakdown.todOffpeakRebateRs ?? 0;
  const pfRebatePctNormal  = breakdown.periods.find(p => p.period === 'NORMAL')?.pfAdjPct   ?? 0;
  const pfRebatePctPeak    = breakdown.periods.find(p => p.period === 'PEAK')?.pfAdjPct     ?? 0;
  const pfRebatePctOffpeak = breakdown.periods.find(p => p.period === 'OFF-PEAK')?.pfAdjPct ?? 0;
  // Per-period average power factor (drives each period's own slab + gauge).
  const pfAvgNormal   = breakdown.periods.find(p => p.period === 'NORMAL')?.pfAvg   ?? 0;
  const pfAvgPeak     = breakdown.periods.find(p => p.period === 'PEAK')?.pfAvg     ?? 0;
  const pfAvgOffpeak  = breakdown.periods.find(p => p.period === 'OFF-PEAK')?.pfAvg ?? 0;
  // Per-period rebate(+)/surcharge(−) amount in Rs (positive = rebate).
  const pfRebateRsNormal  = breakdown.periods.find(p => p.period === 'NORMAL')?.pfAdjRs   ?? 0;
  const pfRebateRsPeak    = breakdown.periods.find(p => p.period === 'PEAK')?.pfAdjRs     ?? 0;
  const pfRebateRsOffpeak = breakdown.periods.find(p => p.period === 'OFF-PEAK')?.pfAdjRs ?? 0;

  // Per-period Max Demand kVA (PDF BE, BF, BG) from the billing engine.
  // WB (west-bengal.ts) computes these from 15-min block classification.
  // Telangana engine sets these to 0 (uses its own peakKva / derivedEngine values).
  const isWb        = tariff.energyUnit !== 'kvah';
  const mdKvaNormal  = breakdown.mdKvaNormal  ?? 0;
  const mdKvaPeak    = breakdown.mdKvaPeak    ?? 0;
  const mdKvaOffpeak = breakdown.mdKvaOffpeak ?? 0;

  const totalEnergyCharge  = piTotalEnergyCharge  || breakdown.ecTotalRs;
  const energyChargeNormal  = piEcNormal           || bkEcNormal;
  const energyChargePeak    = piEcPeak             || bkEcPeak;
  const energyChargeOffpeak = piEcOffpeak          || bkEcOffpeak;
  // Sign convention: positive = rebate (saves money), negative = surcharge (costs money).
  // derivedEngine pfRebateCurr: positive = rebate ✓
  // billing.ts pfAdjTotalRs: negative = rebate (it is subtracted from ecTotal), so negate to match.
  const pfRebateCost = piPfRebateCost !== 0
    ? piPfRebateCost
    : -(breakdown.pfAdjTotalRs);
  // lfRebateCost sign: positive = rebate, negative = surcharge.
  // derivedEngine lfRebateCurrent: positive = rebate ✓
  // billing.ts lfRebateRs: negative = rebate, so negate to match.
  const lfRebateCost = piLfRebateCost !== 0
    ? piLfRebateCost
    : -(breakdown.lfRebateRs);
  // Hero card: "Energy charges excluding demand, duties & meter rental."
  // BUG-1 FIX (B): After derivedEngine.ts Fix A, latest?.totalCost (= piTotalCostMtd)
  // now correctly holds netECCurrent (energy-only EC, no DC/ED/rental) for the WB path.
  // For TGSPDCL (kVAh tariff) totalCost is still grossPayable (includes full monthly DC ≈₹10L+),
  // so we always use the billing engine's pure energy figure for kVAh tariffs.
  // For WB (kWh tariff): piTotalCostMtd is now energy-only → safe to use directly.
  // Billings.tsx then adds DC + ED + rental once (and only once) below in §4 assembly.
  const totalCostMtd = tariff.energyUnit === 'kvah'
    ? breakdown.ecTotalRs   // TGSPDCL: energy-only from billing engine (demand excluded)
    : (breakdown.ecTotalRs + breakdown.pfAdjTotalRs + breakdown.lfRebateRs);
  const lfPct               = breakdown.lfPct      ?? 0;
  const projectedBillRs     = breakdown.projectedBillRs ?? 0;

  // pfAvg: overall weighted PF from breakdown (most accurate).
  const pfAvg = breakdown.pfWeighted || (latest?.pfAvg ?? 0);
  // pfRebatePct: look up from the same overall pfAvg so the displayed PF and rate
  // are consistent (both refer to the same number). Using Normal period as representative.
  const pfRebatePct = pfAdjustmentPct(pfAvg, 'NORMAL');

  // maxDemandKva — identical formula to ContractDemandCard "Peak kVA · This Month":
  // fifteenMinDemandBlocks splits every inter-sample interval across clock-aligned
  // 15-min windows; maxDemandKvaOf returns the largest block's average kVA.
  // This is the same value the dashboard displays (262 kVA in the screenshot).
  const maxDemandKva = useMemo(
    () => maxDemandKvaOf(billingRows.length >= 2 ? billingRows : rows),
    [billingRows, rows],
  );

  // ── Today values ───────────────────────────────────────────────────────────
  // dailyEnergyWh and ecDailyWithoutRebate are running daily accumulators stamped
  // on every row — summing across rows gives the sum of all intermediate values,
  // not the actual total. Use only the LAST today-row (it holds the final value).
  const lastTodayRow = todayRows[todayRows.length - 1] ?? latest;
  const todayBreakdown = useMemo(
    () => todayRows.length >= 2 ? buildBillBreakdown(todayRows, undefined, undefined, contractDemandKva, tariff) : null,
    [todayRows, contractDemandKva, tariff],
  );
  const todayEnergyChargeRs = todayBreakdown?.ecTotalRs ?? 0;
  const todayEnergyKwh = todayBreakdown?.periods.reduce((sum, p) => sum + p.kwh, 0) ?? 0;
  const todayNetEnergyCostRs = todayBreakdown
    ? Math.max(0, todayBreakdown.ecTotalRs + todayBreakdown.pfAdjTotalRs + todayBreakdown.lfRebateRs)
    : 0;

  // ── Today's cost source selection ─────────────────────────────────────────
  // TGSPDCL (kVAh tariff): Pi accumulator fields (dailyNetEC, ecDailyWithoutRebate,
  // dailyCostRs) are UNRELIABLE — they often embed the full monthly demand charge
  // (₹500/kVA × min-demand kVA ≈ ₹10L+), producing wildly inflated intraday
  // figures (e.g. ₹14L for a day with only ₹70k of actual energy).
  // Fix: for kVAh tariffs always use the billing-engine breakdown which computes
  // pure energy charge from trapezoidal kVA integration of today's rows only.
  // WBSEDCL (kWh tariff): keep the existing Pi-accumulator → breakdown fallback
  // chain unchanged so West Bengal billing is not affected.
  const dailyEnergyCostRs = tariff.energyUnit === 'kvah'
    ? todayEnergyChargeRs  // TGSPDCL: always engine-computed, demand-free
    : (lastTodayRow?.ecDailyWithoutRebate ?? 0) || (latest?.dailyEnergyCostRs ?? 0) || todayEnergyChargeRs;
  const rawDailyPfAdjRs   = finite(lastTodayRow?.pfRebateDaily ?? latest?.dailyPfRebateRs);
  const dailyPfRebateRs   = rawDailyPfAdjRs > 0 ? rawDailyPfAdjRs : 0;
  const dailyPfPenaltyRs  = rawDailyPfAdjRs < 0 ? Math.abs(rawDailyPfAdjRs) : 0;
  const dailyLfRebateRs   = finite(lastTodayRow?.lfRebateDaily ?? latest?.dailyLfRebateRs);
  // ── Display-only: today's PF and LF values (no calculation change) ────────
  // Used only to show the intraday PF/LF reading inside the Intraday Bill rows.
  // todayBreakdown is the same engine that computes the monthly pfAvg/lfPct.
  const dailyPfAvg = todayBreakdown?.pfWeighted || finite(lastTodayRow?.runningAvgPF) || 0;
  const dailyLfPct = todayBreakdown?.lfPct ?? finite(lastTodayRow?.loadFactorPct) ?? 0;
  // TGSPDCL: derive daily cost directly from engine breakdown (no demand, no Pi fields).
  // WBSEDCL: preserve existing priority chain (Pi accumulator → engine → manual calc).
  const dailyCostRs = tariff.energyUnit === 'kvah'
    ? todayNetEnergyCostRs || todayEnergyChargeRs  // TGSPDCL: energy-only, demand excluded
    : (finite(lastTodayRow?.dailyNetEC ?? latest?.dailyCostRs)
        || todayNetEnergyCostRs
        || Math.max(0, dailyEnergyCostRs - dailyPfRebateRs + dailyPfPenaltyRs - dailyLfRebateRs));
  // ── Display-only: implied PF and LF adjustments for WB intraday ─────────
  // For WB, dailyNetEC (→ dailyCostRs) has PF+LF already baked in by the Pi,
  // but pfRebateDaily and lfRebateDaily are often null/zero on the Pi row.
  // todayBreakdown (billing engine run on today's rows) gives the correct
  // per-component values with the right signs:
  //   pfAdjTotalRs : negative = rebate, positive = penalty
  //   lfRebateRs   : positive = surcharge (added to bill), negative = rebate
  // Use engine values when Pi fields are absent. Only applies to WB.
  const enginePfAdj = todayBreakdown?.pfAdjTotalRs ?? 0;  // negative = rebate
  const engineLfAdj = todayBreakdown?.lfRebateRs  ?? 0;   // positive = surcharge

  // PF: Pi field → engine fallback (WB only)
  const displayDailyPfRebateRs = dailyPfRebateRs > 0
    ? dailyPfRebateRs
    : (tariff.hasPfAdjustment && dailyPfRebateRs === 0 && dailyPfPenaltyRs === 0 && enginePfAdj < 0)
      ? Math.abs(enginePfAdj)   // engine says rebate
      : 0;
  const displayDailyPfPenaltyRs = dailyPfPenaltyRs > 0
    ? dailyPfPenaltyRs
    : (tariff.hasPfAdjustment && dailyPfRebateRs === 0 && dailyPfPenaltyRs === 0 && enginePfAdj > 0)
      ? enginePfAdj             // engine says penalty
      : 0;

  // LF: Pi field → engine fallback (WB only).
  // dailyLfRebateRs: positive = rebate, negative = surcharge (Pi convention).
  // engineLfAdj:     positive = surcharge, negative = rebate (engine convention — note reversed sign).
  const displayDailyLfSurchargeRs = (tariff.hasLfRebate && dailyLfRebateRs === 0 && engineLfAdj > 0)
    ? engineLfAdj   // engine surcharge
    : (dailyLfRebateRs < 0 ? Math.abs(dailyLfRebateRs) : 0);  // Pi surcharge
  const displayDailyLfRebateRs = (tariff.hasLfRebate && dailyLfRebateRs === 0 && engineLfAdj < 0)
    ? Math.abs(engineLfAdj) // engine rebate
    : (dailyLfRebateRs > 0 ? dailyLfRebateRs : 0);             // Pi rebate
  // TGSPDCL: energy units are kVAh — use the kvah sum from the engine breakdown
  // so "5.54 MWh delivered" reflects apparent energy (kVAh), not active kWh.
  const todayEnergyKvah = todayBreakdown?.totalKvah ?? 0;
  const dailyEnergyKwh = tariff.energyUnit === 'kvah'
    ? (todayEnergyKvah || todayEnergyKwh)  // TGSPDCL: prefer kVAh total, fallback kWh
    : ((((lastTodayRow?.dailyEnergyWh ?? 0) || (latest?.dailyEnergyWh ?? 0)) / 1000) || todayEnergyKwh);

  // ── ToD toggle state ──────────────────────────────────────────────────────
  const todayStr = format(dataEnd, 'yyyy-MM-dd');
  const [todViewPeriod, setTodViewPeriod] = useState<TodViewPeriod>('month');
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo,   setCustomTo]   = useState(todayStr);


  // Filter rows to the selected window — all three branches use actual row-level data
  const todWindowRows = useMemo(() => {
    if (todViewPeriod === 'today') {
      return rows.filter(r => format(r.timestamp, 'yyyy-MM-dd') === todayStr);
    }
    if (todViewPeriod === 'month') {
      const monthStart = format(startOfMonth(dataEnd), 'yyyy-MM-dd');
      return rows.filter(r => format(r.timestamp, 'yyyy-MM-dd') >= monthStart);
    }
    // custom
    if (customFrom && customTo) {
      return rows.filter(r => {
        const d = format(r.timestamp, 'yyyy-MM-dd');
        return d >= customFrom && d <= customTo;
      });
    }
    return rows;
  }, [rows, todViewPeriod, todayStr, customFrom, customTo]);

  // Per-period energy charges for the selected ToD window.
  //
  // Compute from the actual selected window so Today / This Month / Custom all
  // use the same row-level source of truth. This avoids depending on optional
  // daily or month-to-date accumulator fields in the derived rows.
  const todWindowBreakdown = useMemo(() => {
    if (todWindowRows.length < 2) return null;
    return buildBillBreakdown(todWindowRows, undefined, undefined, contractDemandKva, tariff);
  }, [todWindowRows, contractDemandKva, tariff]);

  const todEcNormal = useMemo(() => {
    return todWindowBreakdown?.periods.find(p => p.period === 'NORMAL')?.energyChargeRs ?? bkEcNormal;
  }, [todWindowBreakdown, bkEcNormal]);

  const todEcPeak = useMemo(() => {
    return todWindowBreakdown?.periods.find(p => p.period === 'PEAK')?.energyChargeRs ?? bkEcPeak;
  }, [todWindowBreakdown, bkEcPeak]);

  const todEcOffpeak = useMemo(() => {
    return todWindowBreakdown?.periods.find(p => p.period === 'OFF-PEAK')?.energyChargeRs ?? bkEcOffpeak;
  }, [todWindowBreakdown, bkEcOffpeak]);
  // Total for card header
  const todTotalEc   = todEcNormal + todEcPeak + todEcOffpeak;

  // ── Telangana ToD window breakdown (for the toggle window) ─────────────────
  // The window breakdown computes the same TGSPDCL model fields for the selected
  // time window (Today / This Month / Custom), so the card always shows correct values.
  const todWinEcBaseRs       = todWindowBreakdown?.ecBaseRs            ?? bkEcBaseRs;
  const todWinPeakPenaltyRs  = todWindowBreakdown?.todPeakPenaltyRs    ?? bkTodPeakPenaltyRs;
  const todWinOffpeakRebateRs= todWindowBreakdown?.todOffpeakRebateRs  ?? bkTodOffpeakRebateRs;

  const activeLfBand = LF_REBATE_BANDS.findIndex(b => lfPct >= b.minLf && lfPct < b.maxLf);

  // ── Demand charge — state-aware, engine as single source of truth ────────
  // WBSEDCL (B-IDIT): billing.ts → billing engine fields
  //   BK: Normal DC    = normalKva  × ₹270/kVA  = breakdown.dcNormalRs
  //   BL: Addl DC      = addlKva    × ₹135/kVA  = breakdown.dcPenalRs
  //   BM: Total DC     = BK + BL                 = breakdown.dcRs  (= dcNormalRs + dcPenalRs)
  //
  // TGSPDCL:
  //   AR: Normal DC    = normalKva  × ₹500/kVA  = breakdown.dcNormalRs
  //   AS: Addl DC      = addlKva    × ₹1000/kVA = breakdown.dcPenalRs
  //   AT: Total DC     = AR + AS                 = breakdown.dcRs
  //
  // CRITICAL: demandChargeRs must be dcNormalRs (BK/AR only — the Normal DC line).
  //           totalDemandChargeRs must be dcRs (BM/AT — the pre-computed total).
  //           Using dcRs for demandChargeRs AND then adding dcPenalRs again in
  //           totalDemandChargeRs = dcRs + dcPenalRs double-counts BL/AS.
  const chargeableKva       = isWb ? breakdown.chargeableKva : breakdown.normalKva;
  const demandChargeRs      = breakdown.dcNormalRs;   // BK (WB Normal DC) or AR (TG Normal DC)
  const mdOverrunKva        = breakdown.addlKva;       // BJ (WB) or AQ (TG)
  const mdOverrunPenaltyRs  = breakdown.dcPenalRs;     // BL (WB Addl DC) or AS (TG Addl DC)
  const totalDemandChargeRs = breakdown.dcRs;          // BM = BK+BL (WB) or AT = AR+AS (TG)

  // ── Full bill (PDF §4) ──────────────────────────────────────────────────
  // Net charge before ED = NetEC (energy − PF rebate − LF rebate) + total demand
  // charge, INCLUDING the additional/peak-overshoot demand charge (PDF §4-A, BM).
  const grossBeforeEd        = totalCostMtd + totalDemandChargeRs;
  // ED follows the state's model: per-kVAh (TGSPDCL) or % of net bill (WBSEDCL).
  // WB: always recompute ED from grossBeforeEd directly — breakdown.electricityDutyRs
  // was built on rounded dcRs which no longer matches totalDemandChargeRs (unrounded),
  // so reusing it would create an inconsistency between grossBeforeEd and electricityDutyRs.
  // TGSPDCL: unchanged — breakdown.electricityDutyRs is correct (per-kVAh, not % of bill).
  const electricityDutyRs    = tariff.energyUnit !== 'kvah'
    ? Math.max(0, grossBeforeEd) * tariff.electricityDutyValue                // WB: recompute from live base
    : (breakdown.electricityDutyRs
        || (breakdown.totalKvah * tariff.electricityDutyValue));               // TGSPDCL: unchanged
  const meterRentalRs        = breakdown.meterRentalRs || METER_RENTAL_RS_MONTH;
  const customerChargesRs    = breakdown.customerChargesRs || 0;

  // TGSPDCL: Sub Total = Energy + Demand + ED (all except Customer Charges)
  // WBSEDCL: Sub Total = Energy + Demand (before ED) — unchanged
  const subTotalRs = tariff.energyUnit !== 'kvah'
    ? grossBeforeEd                                                             // WB: sub total before ED
    : grossBeforeEd + electricityDutyRs;                                       // TGSPDCL: sub total includes ED

  // TGSPDCL: Gross Payable = Sub Total + Customer Charges (ED already in sub total)
  // WBSEDCL: Gross Payable = Sub Total + ED + Meter Rental + Customer Charges (unchanged)
  const grossPayable = tariff.energyUnit !== 'kvah'
    ? grossBeforeEd + electricityDutyRs + meterRentalRs + customerChargesRs   // WB unchanged
    : subTotalRs + customerChargesRs + breakdown.acdSurchargeRs;              // TGSPDCL: Sub Total + Customer + ACD
  const tprRs                = grossPayable * (TPR_PCT / 100);
  const netPayable           = grossPayable - tprRs;                           // §4-D

  // ── Month-to-Date Cost (hero card) — full bill accumulated to date ────────
  // Includes ALL bill components: energy charges (after PF + LF adjustments),
  // demand charges, electricity duty, meter rental, and customer charges.
  // This is grossPayable computed from MTD rows — identical to the full bill
  // assembly above, which already uses MTD rows for energy and billing-cycle
  // peak demand for demand charges.
  // Not deducting TPR (a optional rebate, not a cost) or LPSC (only if paid late).
  const mtdTotalBillRs = grossPayable;

  // ── Month projection (PDF §4 F, G) ────────────────────────────────────────
  // Detailed projected breakdown — exposed by buildBillBreakdown for the UI
  const projectedGrossEnergy  = breakdown.projectedGrossEcRs
    ?? (totalEnergyCharge / (daysPassed || 1)) * daysInMonth;
  const projectedPfAdjRs      = breakdown.projectedPfAdjRs  ?? 0;  // positive = rebate
  const projectedLfAdjRs      = breakdown.projectedLfAdjRs  ?? 0;  // positive = rebate

  // WB: recompute projected ED and net payable using totalDemandChargeRs (unrounded)
  // so the projected bill is consistent with MTD hero and Max Demand Analysis card.
  // billing.ts projection uses dcRs (rounded) internally, so we override here for WB.
  // TGSPDCL: use billing engine values unchanged.
  const projectedEdRs = tariff.energyUnit !== 'kvah'
    ? (() => {
        // Projected net energy (after PF rebate + LF penalty, same as billing.ts)
        const projNetEc      = projectedGrossEnergy - projectedPfAdjRs + projectedLfAdjRs;
        // Sub-total = proj net energy + unrounded demand (matches display card)
        const projSubTotal   = Math.max(0, projNetEc + totalDemandChargeRs);
        return projSubTotal * tariff.electricityDutyValue;              // 12.5% of sub-total
      })()
    : (breakdown.projectedEdRs ?? electricityDutyRs);                  // TGSPDCL: unchanged

  const projectedTprProjected = tariff.energyUnit !== 'kvah'
    ? (() => {
        const projNetEc    = projectedGrossEnergy - projectedPfAdjRs + projectedLfAdjRs;
        const projSubTotal = Math.max(0, projNetEc + totalDemandChargeRs);
        const projED       = projSubTotal * tariff.electricityDutyValue;
        const projGross    = projSubTotal + projED + meterRentalRs + customerChargesRs;
        return projGross * (TPR_PCT / 100);
      })()
    : (breakdown.projectedTprRs ?? tprRs);                             // TGSPDCL: unchanged

  // WB: rebuild projectedNetPayable from the corrected (unrounded demand) components.
  // TGSPDCL: prefer billing engine's projectedBillRs unchanged.
  const projectedNetPayable = tariff.energyUnit !== 'kvah'
    ? (() => {
        const projNetEc    = projectedGrossEnergy - projectedPfAdjRs + projectedLfAdjRs;
        const projSubTotal = Math.max(0, projNetEc + totalDemandChargeRs);
        const projED       = projSubTotal * tariff.electricityDutyValue;
        const projGross    = projSubTotal + projED + meterRentalRs + customerChargesRs;
        return projGross - projGross * (TPR_PCT / 100);                // net after TPR
      })()
    : (projectedBillRs > 0
        ? projectedBillRs
        : (netPayable / (daysPassed || 1)) * daysInMonth);             // TGSPDCL: unchanged

  // Projected per-period kVAh from the billing engine — passed to the PDF so the
  // OPTIONS/RATE table shows full-month projected units (matching the dashboard
  // "Projected energy charge (gross)" row), not just MTD partial values.
  const projectedKvahNormal  = breakdown.projectedKvah?.['NORMAL']   ?? 0;
  const projectedKvahPeak    = breakdown.projectedKvah?.['PEAK']     ?? 0;
  const projectedKvahOffpeak = breakdown.projectedKvah?.['OFF-PEAK'] ?? 0;

  // kWh values derived from energy charges to guarantee consistency
  // (energy charge ÷ rate = kWh).  Using accumulator-based charges as the
  // single source of truth so the PDF meter table never disagrees with the
  // energy charge table.
  const pdfKwhNormal  = energyChargeNormal  > 0
    ? energyChargeNormal  / TARIFF_RATES.NORMAL
    : bkKwhNormal;
  const pdfKwhPeak    = energyChargePeak    > 0
    ? energyChargePeak    / TARIFF_RATES.PEAK
    : bkKwhPeak;
  const pdfKwhOffpeak = energyChargeOffpeak > 0
    ? energyChargeOffpeak / TARIFF_RATES['OFF-PEAK']
    : bkKwhOffpeak;

  // ── Daily cost timeline (from per-row dailyCostRs, one point per day) ─────
  // ── Hourly cost waterfall (today only, for intraday view) ─────────────────
  //
  // SPIKE ROOT CAUSE (Ralpro Main / TGSPDCL):
  //   The Pi accumulator fields ecNormalDaily / ecPeakDaily / ecOffpeakDaily are
  //   computed in derivedEngine from dayAccVahByToD — which accumulates VAh via
  //   register deltas (vahReceived[curr] − vahReceived[prev]).  On the LK4410 the
  //   vahReceived register occasionally produces anomalous large jumps (meter reset
  //   artifacts, communication gaps, or mismatched units in the raw sheet).  A
  //   single anomalous jump inflates one row's dayAccVahByToD entry dramatically,
  //   which makes ecNormalDaily/ecPeakDaily spike for that one row.  Because the
  //   chart draws deltas between consecutive rows, the spike appears as one enormous
  //   bar followed by a near-zero bar — exactly the "two large spikes" pattern.
  //
  // FIX:
  //   For kVAh tariffs (TGSPDCL), skip ALL Pi-accumulator paths (ecNormalDaily,
  //   dailyEnergyWhNormal, ecDailyWithoutRebate) and go straight to per-interval
  //   trapezoidal integration of kvaTotal.  This mirrors billing.ts kvahDelivered()
  //   (which already uses the same guard) and is immune to register anomalies
  //   because kvaTotal is an instantaneous power reading, not a cumulative register.
  //
  //   For WB (kWh tariff) the existing priority chain is unchanged.
  const hourlyCost = useMemo(() => {
    const sorted = [...todayRows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const byHour = new Map<number, { normal: number; peak: number; offPeak: number }>();
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const h = curr.hour ?? curr.timestamp.getHours();
      if (!byHour.has(h)) byHour.set(h, { normal: 0, peak: 0, offPeak: 0 });
      const slot = byHour.get(h)!;

      if (tariff.energyUnit === 'kvah') {
        // TGSPDCL: trapezoidal integration of kvaTotal only — bypasses all Pi
        // accumulators which can carry register-reset spikes on the LK4410.
        const dtH = (curr.timestamp.getTime() - prev.timestamp.getTime()) / 3_600_000;
        if (dtH > 0 && dtH <= 0.5) {
          const avgKva = (finite(prev.kvaTotal) + finite(curr.kvaTotal)) / 2;
          const kvah   = avgKva * dtH;
          const period = curr.todPeriod ?? 'NORMAL';
          if      (period === 'PEAK')     slot.peak    += kvah * TARIFF_RATES.PEAK;
          else if (period === 'OFF-PEAK') slot.offPeak += kvah * TARIFF_RATES['OFF-PEAK'];
          else                            slot.normal  += kvah * TARIFF_RATES.NORMAL;
        }
        continue;
      }

      // WB (kWh tariff): preserve existing priority chain unchanged.
      const normalCostDelta = Math.max(0, finite(curr.ecNormalDaily) - finite(prev.ecNormalDaily));
      const peakCostDelta = Math.max(0, finite(curr.ecPeakDaily) - finite(prev.ecPeakDaily));
      const offPeakCostDelta = Math.max(0, finite(curr.ecOffpeakDaily) - finite(prev.ecOffpeakDaily));

      if (normalCostDelta || peakCostDelta || offPeakCostDelta) {
        slot.normal += normalCostDelta;
        slot.peak += peakCostDelta;
        slot.offPeak += offPeakCostDelta;
        continue;
      }

      const normalWhDelta = Math.max(0, finite(curr.dailyEnergyWhNormal) - finite(prev.dailyEnergyWhNormal));
      const peakWhDelta = Math.max(0, finite(curr.dailyEnergyWhPeak) - finite(prev.dailyEnergyWhPeak));
      const offPeakWhDelta = Math.max(0, finite(curr.dailyEnergyWhOffpeak) - finite(prev.dailyEnergyWhOffpeak));

      if (normalWhDelta || peakWhDelta || offPeakWhDelta) {
        slot.normal += (normalWhDelta / 1000) * TARIFF_RATES.NORMAL;
        slot.peak += (peakWhDelta / 1000) * TARIFF_RATES.PEAK;
        slot.offPeak += (offPeakWhDelta / 1000) * TARIFF_RATES['OFF-PEAK'];
        continue;
      }

      const totalDailyDelta = Math.max(0, finite(curr.ecDailyWithoutRebate) - finite(prev.ecDailyWithoutRebate));
      if (totalDailyDelta === 0) {
        const registerUnitDelta = Math.max(0, finite(curr.energyKwh) - finite(prev.energyKwh));
        const dtH = (curr.timestamp.getTime() - prev.timestamp.getTime()) / 3_600_000;
        const integratedUnit = (() => {
          if (dtH <= 0 || dtH > 0.5) return 0;
          const avgKw = (finite(prev.wattsTotal) + finite(curr.wattsTotal)) / 2000;
          return avgKw * dtH; // kWh
        })();
        const unitDelta = registerUnitDelta || integratedUnit;
        const period = curr.todPeriod ?? 'NORMAL';
        if (period === 'PEAK') slot.peak += unitDelta * TARIFF_RATES.PEAK;
        else if (period === 'OFF-PEAK') slot.offPeak += unitDelta * TARIFF_RATES['OFF-PEAK'];
        else slot.normal += unitDelta * TARIFF_RATES.NORMAL;
        continue;
      }

      const period = curr.todPeriod ?? 'NORMAL';
      if (period === 'PEAK') slot.peak += totalDailyDelta;
      else if (period === 'OFF-PEAK') slot.offPeak += totalDailyDelta;
      else slot.normal += totalDailyDelta;
    }
    return Array.from(byHour.entries())
      .sort(([a], [b]) => a - b)
      .map(([h, v]) => ({ label: `${String(h).padStart(2, '0')}:00`, ...v }));
  }, [TARIFF_RATES, tariff.energyUnit, tariff.todPeriodFor, todayRows]);

  // PF slab highlight is computed per-period (activeSlabNormal/Peak/Offpeak below).
  // Per-period active slab — each ToD period lands on its own PF range.
  const slabFor = (pf: number) =>
    PF_ADJUSTMENT_TABLE.findIndex((r: PfAdjustmentRow) => pf >= r.minPf && pf < r.maxPf);
  const activeSlabNormal  = useMemo(() => slabFor(pfAvgNormal),  [pfAvgNormal]);
  const activeSlabPeak    = useMemo(() => slabFor(pfAvgPeak),    [pfAvgPeak]);
  const activeSlabOffpeak = useMemo(() => slabFor(pfAvgOffpeak), [pfAvgOffpeak]);

  if (!latest) {
    return (
      <div className="space-y-5 pt-3 pb-2">
        <PageHeader title="Billing" description={`${tariff.discName} · CORTEX invoice estimate`} />
        <Card><p className="text-xs text-ink-400 text-center py-8">No data available yet.</p></Card>
      </div>
    );
  }
  return (
    <div className="space-y-5 pt-3 pb-2 animate-fade-in">
      <PageHeader
        title="Billing"
        description={`${tariff.discName} ${tariff.tariffCategory} · ${format(startOfMonth(dataEnd), 'MMMM yyyy')} · updated every 5 min`}
      />

      {status === 'error' && rows.length ? <StaleDataBanner /> : null}
      {/* ── Hero: MTD net cost ──────────────────────────────────────────── */}
      <Card padded className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl" />
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 60%)' }} />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-white/70">
              Month-to-Date Cost
            </span>
            <IndianRupee className="w-4 h-4 text-white/60" />
          </div>
          <div className="num text-4xl font-bold text-white leading-none mb-1">
            {rs(mtdTotalBillRs)}
          </div>
          <p className="text-[11px] text-white/60 mb-4">
            {tariff.energyUnit === 'kvah'
              ? 'Energy charges + Demand charges + Electricity Duty + Customer charges · accumulated till date'
              : 'Energy charges + Power Factor adjustment + Load Factor rebates + Demand charges + Electricity Duty + Meter rental · accumulated till date'
            }
          </p>
          {/* Progress bar: daysPassed / daysInMonth */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full"
                style={{ width: `${(daysPassed / daysInMonth) * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-white/70 whitespace-nowrap">
              {daysPassed} / {daysInMonth} days
            </span>
          </div>
        </div>
      </Card>

      {/* ── KPI strip — WB: 6 items (PF/LF + today + projected); TG: 2 items ── */}
      <div className="grid grid-cols-2 gap-3">
        {tariff.hasPfAdjustment && (
          <KpiCard
            label="Power Factor (Month-to-Date)"
            value={formatPF(pfAvg)}
            icon={Activity}
            highlight={pfAvg >= 0.95 ? 'good' : pfAvg < 0.80 ? 'danger' : 'neutral'}
          />
        )}
        {tariff.hasPfAdjustment && (
          <KpiCard
            label={pfRebateCost > 0 ? 'Power Factor Rebate' : pfRebateCost < 0 ? 'Power Factor Penalty' : 'Power Factor Adjustment'}
            value={rs(Math.abs(pfRebateCost))}
            sub={pfRebateCost > 0 ? 'rebate earned' : pfRebateCost < 0 ? 'penalty' : 'no adjustment'}
            icon={TrendingDown}
            highlight={pfRebateCost > 0 ? 'good' : pfRebateCost < 0 ? 'danger' : 'neutral'}
          />
        )}
        {tariff.hasLfRebate && (
          <KpiCard
            label="Load Factor"
            value={`${lfPct.toFixed(1)}%`}
            sub="month-to-date"
            icon={Gauge}
            highlight="neutral"
          />
        )}
        {tariff.hasLfRebate && (
          <KpiCard
            label={lfRebateCost > 0 ? 'Load Factor Rebate' : lfRebateCost < 0 ? 'Load Factor Penalty' : 'Load Factor Adjustment'}
            value={rs(Math.abs(lfRebateCost))}
            sub={lfRebateCost > 0 ? 'rebate earned' : lfRebateCost < 0 ? 'surcharge applied' : 'no adjustment'}
            icon={TrendingDown}
            highlight={lfRebateCost > 0 ? 'good' : lfRebateCost < 0 ? 'danger' : 'neutral'}
          />
        )}
        <KpiCard
          label="Today's Cost"
          value={rs(dailyCostRs)}
          sub={`${formatKwh(dailyEnergyKwh)} delivered`}
          icon={CalendarDays}
          highlight="neutral"
        />
        <KpiCard
          label="Projected Bill"
          value={projectedNetPayable > 0 ? rs(projectedNetPayable) : '—'}
          sub="net incl. all charges"
          icon={Target}
          highlight="neutral"
        />
      </div>

      {/* ── Energy Charges by ToD ───────────────────────────────────────── */}
      <SectionTitle>Energy Charges · Time-of-Day Split</SectionTitle>
      <Card padded>
        <CardHeader
          title="Time-of-Day Split"
          description="Energy charges accumulated by tariff period"
          trailing={
            <span className="num text-sm font-bold text-ink-900">{rs(todTotalEc)}</span>
          }
        />

        {/* Toggle */}
        <TodToggle
          active={todViewPeriod}
          onChange={setTodViewPeriod}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(f, t) => { setCustomFrom(f); setCustomTo(t); }}
          dataEndDate={dataEnd}
        />


        {/* Window data label */}
        <p className="text-[10px] text-ink-400 mb-3 -mt-1 leading-relaxed">
          {todViewPeriod === 'today'
            ? `Showing today's charges (${todWindowRows.length} readings)`
            : todViewPeriod === 'month'
            ? `Showing month-to-date charges (${todWindowRows.length} readings)`
            : customFrom && customTo
            ? `Showing ${customFrom} to ${customTo} (${todWindowRows.length} readings)`
            : 'Select a date range above'}
        </p>

        {/* Donut chart + legend — uses toggle-selected values */}
        {(() => {
          const totalTod = todEcNormal + todEcPeak + todEcOffpeak;
          const donutData = [
            { name: 'Normal',   value: todEcNormal,  color: TOD_COLORS['NORMAL']   },
            { name: 'Peak',     value: todEcPeak,    color: TOD_COLORS['PEAK']     },
            { name: 'Off-Peak', value: todEcOffpeak, color: TOD_COLORS['OFF-PEAK'] },
          ].filter(d => d.value > 0);
          // Fallback: render a neutral ring when there's no data yet so the
          // chart never collapses to an empty box.
          const chartData = donutData.length
            ? donutData
            : [{ name: 'No data', value: 1, color: '#E2E8F0' }];
          return (
            <>
              <div className="flex items-center gap-4">
                <div className="w-32 h-32 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%" cy="50%"
                        innerRadius={34} outerRadius={52}
                        paddingAngle={donutData.length > 1 ? 3 : 0}
                        minAngle={donutData.length ? 4 : 0}
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth={2}
                        isAnimationActive={false}
                      >
                        {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {[
                    { period: 'NORMAL'   as const, charge: todEcNormal  },
                    { period: 'PEAK'     as const, charge: todEcPeak    },
                    { period: 'OFF-PEAK' as const, charge: todEcOffpeak },
                  ].map(({ period, charge }) => {
                    const pctVal = totalTod > 0 ? (charge / totalTod) * 100 : 0;
                    return (
                      <div key={period}>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: TOD_COLORS[period] }} />
                            <span className="text-[10px] font-medium text-ink-700">
                              {TOD_WINDOWS[period].label}
                            </span>
                            <span className="text-[9px] text-ink-400">
                              {TOD_WINDOWS[period].range}
                            </span>
                          </div>
                          <span className="num text-[10px] font-semibold text-ink-900">{rs(charge)}</span>
                        </div>
                        <div className="h-1 bg-ink-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pctVal}%`, background: TOD_COLORS[period] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Detailed rows — TGSPDCL: Base EC + ToD adjustments; WB: per-period */}
              <div className="mt-3 pt-3 border-t border-ink-100 space-y-0.5">
                {tariff.energyUnit === 'kvah' ? (
                  // ── TGSPDCL billing model: Base EC + ToD Peak Penalty − ToD Off-Peak Rebate ──
                  <>
                    <BillRow
                      label={`Base Energy Charge · ₹${TARIFF_RATES.NORMAL.toFixed(2)}/kVAh × Total kVAh`}
                      value={rs(todWinEcBaseRs)}
                      indent
                    />
                    <BillRow
                      label={`ToD Peak Penalty · +₹${(TARIFF_RATES.PEAK - TARIFF_RATES.NORMAL).toFixed(2)}/kVAh · ${TOD_WINDOWS['PEAK'].range}`}
                      value={`+${rs(todWinPeakPenaltyRs)}`}
                      indent penalty
                    />
                    <BillRow
                      label={`ToD Off-Peak Rebate · −₹${(TARIFF_RATES.NORMAL - TARIFF_RATES['OFF-PEAK']).toFixed(2)}/kVAh · ${TOD_WINDOWS['OFF-PEAK'].range}`}
                      value={`-${rs(todWinOffpeakRebateRs)}`}
                      indent savings
                    />
                    <div className="h-px bg-ink-100 my-1" />
                    <BillRow label={`Total Energy Charge${todViewPeriod === 'today' ? ' · Today' : todViewPeriod === 'custom' ? ' · Custom range' : ' · Month-to-Date'}`} value={rs(todTotalEc)} bold />
                  </>
                ) : (
                  // ── WBSEDCL: per-period rows (unchanged) ──
                  <>
                    <BillRow
                      label={`Normal · ₹${TARIFF_RATES.NORMAL.toFixed(2)}/${energyUnitLabel} · ${TOD_WINDOWS['NORMAL'].range}`}
                      value={rs(todEcNormal)}
                      indent
                    />
                    <BillRow
                      label={`Peak · ₹${TARIFF_RATES.PEAK.toFixed(2)}/${energyUnitLabel} · ${TOD_WINDOWS['PEAK'].range}`}
                      value={rs(todEcPeak)}
                      indent
                    />
                    <BillRow
                      label={`Off-Peak · ₹${TARIFF_RATES['OFF-PEAK'].toFixed(2)}/${energyUnitLabel} · ${TOD_WINDOWS['OFF-PEAK'].range}`}
                      value={rs(todEcOffpeak)}
                      indent
                    />
                    <div className="h-px bg-ink-100 my-1" />
                    <BillRow label={`Total Energy Charge${todViewPeriod === 'today' ? ' · Today' : todViewPeriod === 'custom' ? ' · Custom range' : ' · Month-to-Date'}`} value={rs(todTotalEc)} bold />
                  </>
                )}
              </div>
            </>
          );
        })()}
      </Card>

      {/* ── PF Adjustment — TGSPDCL (Telangana): hidden. WBSEDCL: shown. ── */}
      {tariff.hasPfAdjustment && (
        <>
          <SectionTitle>Power Factor Adjustment</SectionTitle>
          <Card padded>
            <CardHeader
              title={pfRebateCost > 0 ? 'Power Factor Rebate' : pfRebateCost < 0 ? 'Power Factor Penalty' : 'Power Factor Adjustment'}
              trailing={
                <span className={cn(
                  'num text-sm font-bold',
                  pfRebateCost > 0 ? 'text-emerald-600' : pfRebateCost < 0 ? 'text-red-600' : 'text-ink-500',
                )}>
                  {`${pfRebateCost > 0 ? '-' : ''}${rs(Math.abs(pfRebateCost))}`}
                </span>
              }
              description={`${tariff.discName} — consumers under TOD Tariff (CD > 50 kVA); % of energy charge per ToD period`}
            />

            {/* Live PF gauge — one bar per ToD period */}
            <div className="mb-4 space-y-2">
              <PfPeriodGauge label="Normal"   pf={pfAvgNormal}  adjPct={pfRebatePctNormal}  color={TOD_COLORS.NORMAL} />
              <PfPeriodGauge label="Peak"     pf={pfAvgPeak}    adjPct={pfRebatePctPeak}    color={TOD_COLORS.PEAK} />
              <PfPeriodGauge label="Off-Peak" pf={pfAvgOffpeak} adjPct={pfRebatePctOffpeak} color={TOD_COLORS['OFF-PEAK']} />
            </div>

            {/* Slab table — each ToD period's own slab cell is highlighted */}
            <div className="space-y-0.5">
              <div className="grid grid-cols-4 gap-1 px-2 pb-1">
                {['Power Factor Range', 'Normal', 'Peak', 'Off-Peak'].map(h => (
                  <span key={h} className="text-[9px] font-bold uppercase tracking-wider text-ink-400 text-center first:text-left">{h}</span>
                ))}
              </div>
              {PF_ADJUSTMENT_TABLE.map((row: PfAdjustmentRow, i: number) => (
                <PfTableRow
                  key={i}
                  row={row}
                  activeNormal={i === activeSlabNormal}
                  activePeak={i === activeSlabPeak}
                  activeOffpeak={i === activeSlabOffpeak}
                />
              ))}
            </div>

            {/* Footer — rebate/surcharge box per ToD period + applied total */}
            <div className="mt-3 pt-3 border-t border-ink-100">
              <div className="grid grid-cols-3 gap-2">
                <PfPeriodBox label="Normal"   amount={pfRebateRsNormal}  color={TOD_COLORS.NORMAL}        rs={rs} />
                <PfPeriodBox label="Peak"     amount={pfRebateRsPeak}    color={TOD_COLORS.PEAK}          rs={rs} />
                <PfPeriodBox label="Off-Peak" amount={pfRebateRsOffpeak} color={TOD_COLORS['OFF-PEAK']}   rs={rs} />
              </div>
              <div className="mt-2 pt-2 border-t border-ink-100">
                <BillRow
                  label="Power Factor rebate / penalty MTD"
                  value={pfRebateCost >= 0 ? `-${rs(pfRebateCost)}` : `+${rs(Math.abs(pfRebateCost))}`}
                  sub={pfRebateCost > 0 ? "deducted from this month's bill" : pfRebateCost < 0 ? "added to this month's bill" : 'no adjustment'}
                  savings={pfRebateCost > 0}
                  penalty={pfRebateCost < 0}
                  bold
                />
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ── LF Rebate — TGSPDCL (Telangana): hidden. WBSEDCL: shown. ──── */}
      {tariff.hasLfRebate && (
        <>
          <SectionTitle>Load Factor Adjustment</SectionTitle>
          <Card padded>
            <CardHeader
              title={lfRebateCost > 0 ? 'Load Factor Rebate' : lfRebateCost < 0 ? 'Load Factor Penalty' : 'Load Factor Adjustment'}
              description={`${tariff.discName} — applied for Load Factor ≥ 65%`}
              trailing={
                <span className={cn(
                  'num text-sm font-bold',
                  lfRebateCost > 0 ? 'text-emerald-600' : lfRebateCost < 0 ? 'text-red-600' : 'text-ink-500',
                )}>
                  {lfRebateCost > 0
                    ? `-${rs(lfRebateCost)}`
                    : lfRebateCost < 0
                      ? `+${rs(Math.abs(lfRebateCost))}`
                      : '—'}
                </span>
              }
            />

            {/* Live LF gauge */}
            <div className="mb-4 p-3 rounded-xl bg-ink-50 flex items-center gap-4">
              <div className="flex flex-col items-center">
                <span className={cn(
                  'num text-2xl font-bold',
                  lfPct >= 70 ? 'text-emerald-600' : lfPct >= 65 ? 'text-ink-600' : 'text-red-600',
                )}>
                  {lfPct.toFixed(1)}%
                </span>
                <span className="text-[9px] text-ink-400 uppercase tracking-wide">Load Factor</span>
              </div>
              <div className="flex-1 h-3 bg-ink-200 rounded-full overflow-hidden relative">
                {/* Gauge spans 0–100% LF. Each segment width = (maxLf - minLf) clamped to 100. */}
                <div className="absolute inset-0 flex">
                  {LF_REBATE_BANDS.map((b, i) => {
                    const segWidth = (Math.min(b.maxLf, 100) - b.minLf);
                    return (
                      <div
                        key={i}
                        className={cn('h-full', b.surcharge ? 'bg-red-300' : b.rebate ? 'bg-emerald-300' : 'bg-white')}
                        style={{ width: `${segWidth}%` }}
                      />
                    );
                  })}
                </div>
                <div
                  className="absolute top-0 w-1 h-full bg-ink-900 rounded-full shadow-md"
                  style={{ left: `${Math.min(99, Math.max(0, lfPct))}%` }}
                />
              </div>
              <div className="flex flex-col items-end">
                <span className={cn(
                  'num text-base font-bold',
                  lfRebateCost > 0 ? 'text-emerald-600' : lfRebateCost < 0 ? 'text-red-600' : 'text-ink-500',
                )}>
                  {lfRebateCost > 0
                    ? `-${rs(lfRebateCost)}`
                    : lfRebateCost < 0
                      ? `+${rs(Math.abs(lfRebateCost))}`
                      : '—'}
                </span>
                <span className="text-[9px] text-ink-400 uppercase tracking-wide">
                  {lfRebateCost > 0 ? 'Rebate' : lfRebateCost < 0 ? 'Surcharge' : 'No rebate'}
                </span>
              </div>
            </div>

            {/* LF band table */}
            <div className="space-y-0.5">
              <div className="grid grid-cols-2 gap-1 px-2 pb-1">
                {['LF Band', 'Rebate (Rs/kWh)'].map(h => (
                  <span key={h} className="text-[9px] font-bold uppercase tracking-wider text-ink-400 first:text-left text-center">{h}</span>
                ))}
              </div>
              {LF_REBATE_BANDS.map((band, i) => (
                <LfTableRow key={i} band={band} active={i === activeLfBand} />
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-ink-100 space-y-0.5">
              <BillRow
                label="Load Factor adjustment MTD"
                value={lfRebateCost > 0
                  ? `-${rs(lfRebateCost)}`
                  : lfRebateCost < 0
                    ? `+${rs(Math.abs(lfRebateCost))}`
                    : rs(0)}
                sub={lfRebateCost > 0 ? 'deducted from bill' : lfRebateCost < 0 ? 'surcharge added to bill' : 'no adjustment applicable'}
                savings={lfRebateCost > 0}
                penalty={lfRebateCost < 0}
                bold
              />
            </div>
          </Card>
        </>
      )}

      {/* ── Max Demand ─────────────────────────────────────────────────── */}
      <SectionTitle>Max Demand Analysis</SectionTitle>
      <Card padded>

        {/* ── Top header row: title + status badge ─────────────────────── */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-sm font-bold text-ink-900 leading-tight">Maximum demand</p>
            <p className="text-[10px] text-ink-400 mt-0.5">
              Contract demand: <span className="font-semibold text-ink-600">{contractDemandKva} kVA</span>
              {' · '}Chargeable min: <span className="font-semibold text-ink-600">{n2(minChargeableKva)} kVA</span>
            </p>
          </div>
          <span className={cn(
            'shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full',
            maxDemandKva > contractDemandKva
              ? 'bg-red-100 text-red-700 border border-red-200'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
          )}>
            {maxDemandKva > contractDemandKva
              ? <><AlertTriangle className="w-3 h-3" /> Demand Penalty Applied</>
              : <><ShieldCheck className="w-3 h-3" /> Within Limit</>
            }
          </span>
        </div>

        {/* ── Peak kVA hero number ──────────────────────────────────────── */}
        <div className={cn(
          'rounded-2xl p-4 mb-4',
          maxDemandKva > contractDemandKva
            ? 'bg-gradient-to-br from-red-50 to-orange-50 border border-red-200'
            : 'bg-gradient-to-br from-brand-50 to-blue-50 border border-brand-100',
        )}>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-400 mb-0.5">
                Billing Cycle Peak
              </p>
              {maxDemandRow && (
                <div className="flex items-center gap-1 mb-1">
                  <Clock className="w-3 h-3 text-ink-400 shrink-0" />
                  <span className="text-[10px] text-ink-500">
                    {format(maxDemandRow.timestamp, 'dd MMM yyyy, hh:mm a')}
                  </span>
                </div>
              )}
              <p className={cn(
                'num text-4xl font-extrabold leading-none',
                maxDemandKva > contractDemandKva ? 'text-red-600' : 'text-brand-700',
              )}>
                {n2(maxDemandKva)}
                <span className="text-lg font-semibold ml-1 text-ink-500">kVA</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-ink-400">Contract limit</p>
              <p className="num text-xl font-bold text-ink-700">{contractDemandKva} kVA</p>
            </div>
          </div>
        </div>

        {/* ── Charge summary rows ───────────────────────────────────────── */}
        {(() => {
          // PDF BI: Chargeable kVA = max(min(Max kVA, CD), minDemandPct × CD)
          // For WB: use billing engine's ROUNDED kVA values (breakdown.chargeableKva,
          // breakdown.addlKva, breakdown.dcNormalRs, breakdown.dcPenalRs) so that:
          //   normalDcDisplay + addlDcDisplay == totalDcDisplay (no rounding gap)
          //   and these values match the PDF exactly (PDF uses the same engine fields).
          //
          // The previous code used unrounded floats (maxDemandKva, mdKvaPeak) for line
          // items but breakdown.dcRs (rounded) for the total — causing the displayed
          // subtraction to not equal the total (e.g. 94,661.65 + 23,570.82 ≠ 1,15,155).
          //
          // TGSPDCL: unchanged — already used breakdown fields correctly.
          const chargeableKvaDisplay = isWb
            ? breakdown.chargeableKva                    // WB: engine rounded (e.g. 351)
            : Math.max(Math.min(maxDemandKva, contractDemandKva), minChargeableKva); // TG: float OK (display only)
          const addlKvaDisplay = isWb
            ? breakdown.addlKva                          // WB: engine rounded (e.g. 151)
            : Math.max(0, maxDemandKva - contractDemandKva);
          const normalDcDisplay = isWb
            ? breakdown.dcNormalRs                       // WB: BK = chargeableKva × 270 (integer)
            : chargeableKvaDisplay * DEMAND_CHARGE_RS_PER_KVA;
          const addlDcDisplay = isWb
            ? breakdown.dcPenalRs                        // WB: BL = addlKva × 135 (integer)
            : addlKvaDisplay * DEMAND_PENAL_RS_PER_KVA;
          const totalDcDisplay = normalDcDisplay + addlDcDisplay; // always = dcNormalRs + dcPenalRs = dcRs ✓
          const isBelowMin = maxDemandKva < minChargeableKva;
          const hasAddl    = addlKvaDisplay > 0;

          return (
        <div className="rounded-xl border border-ink-100 overflow-hidden">

          {/* ── Per-ToD Max Demand breakdown (PDF BE / BF / BG) — WB only ── */}
          {isWb && (
          <div className="px-3 pt-2.5 pb-1 border-b border-ink-100 bg-ink-50/50">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-400 mb-2">
              Max Demand by Time-of-Day Period
            </p>
            <div className="grid grid-cols-3 gap-2 mb-1.5">
              {/* NORMAL */}
              <div className="rounded-lg border border-ink-100 bg-white px-2 py-1.5">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tariff.todColors.NORMAL }} />
                  <span className="text-[9px] font-bold uppercase tracking-wide text-ink-400">Normal</span>
                </div>
                <p className="num text-sm font-extrabold text-ink-900 leading-none">
                  {mdKvaNormal > 0 ? n2(mdKvaNormal) : '—'}
                </p>
                <p className="text-[9px] text-ink-400 mt-0.5">kVA</p>
                <p className="text-[9px] text-ink-400 leading-tight mt-0.5">
                  {tariff.todWindows.NORMAL?.range ?? ''}
                </p>
              </div>
              {/* PEAK */}
              <div className={cn(
                'rounded-lg border px-2 py-1.5',
                mdKvaPeak > contractDemandKva ? 'bg-red-50 border-red-200' : 'bg-white border-ink-100',
              )}>
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tariff.todColors.PEAK }} />
                  <span className="text-[9px] font-bold uppercase tracking-wide text-ink-400">Peak</span>
                  {mdKvaPeak > contractDemandKva && (
                    <span className="text-[8px] font-bold text-red-600 ml-auto">▲ CD</span>
                  )}
                </div>
                <p className={cn(
                  'num text-sm font-extrabold leading-none',
                  mdKvaPeak > contractDemandKva ? 'text-red-600' : 'text-ink-900',
                )}>
                  {mdKvaPeak > 0 ? n2(mdKvaPeak) : '—'}
                </p>
                <p className="text-[9px] text-ink-400 mt-0.5">kVA</p>
                <p className="text-[9px] text-ink-400 leading-tight mt-0.5">
                  {tariff.todWindows.PEAK?.range ?? ''}
                </p>
              </div>
              {/* OFF-PEAK */}
              <div className="rounded-lg border border-ink-100 bg-white px-2 py-1.5">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tariff.todColors['OFF-PEAK'] }} />
                  <span className="text-[9px] font-bold uppercase tracking-wide text-ink-400">Off-Peak</span>
                </div>
                <p className="num text-sm font-extrabold text-ink-900 leading-none">
                  {mdKvaOffpeak > 0 ? n2(mdKvaOffpeak) : '—'}
                </p>
                <p className="text-[9px] text-ink-400 mt-0.5">kVA</p>
                <p className="text-[9px] text-ink-400 leading-tight mt-0.5">
                  {tariff.todWindows['OFF-PEAK']?.range ?? ''}
                </p>
              </div>
            </div>
            <p className="text-[9px] text-ink-400 mb-1.5">
              PDF columns BE / BF / BG · max of all 15-min averaged blocks per period
            </p>
          </div>
          )}

          {/* ── Chargeable demand (PDF BI) ────────────────────────────────── */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-ink-100">
            <div>
              <span className="text-xs text-ink-700">Chargeable demand</span>
              {isWb && (
                <span className="text-[10px] text-ink-400 ml-1">(PDF BI · max(BH, {Math.round(tariff.minDemandPct * 100)}% CD))</span>
              )}
              {isBelowMin && (
                <p className="text-[10px] text-amber-600 mt-0.5">
                  Metered {n2(maxDemandKva)} kVA · below min → charged on {n2(chargeableKvaDisplay)} kVA
                </p>
              )}
            </div>
            <span className="num text-xs font-bold text-ink-900">{n2(chargeableKvaDisplay)} kVA</span>
          </div>

          {/* ── Normal demand charge (PDF BK) ─────────────────────────────── */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-ink-100">
            <div>
              <span className="text-xs text-ink-700">Normal demand charge</span>
              <span className="text-[10px] text-ink-400 ml-1">(₹{DEMAND_CHARGE_RS_PER_KVA}/kVA/month)</span>
              {isBelowMin && (
                <span className="text-[10px] text-amber-600 ml-1">· min demand applies</span>
              )}
            </div>
            <span className="num text-xs font-bold text-ink-900">{rs(normalDcDisplay)}</span>
          </div>

          {/* ── Additional demand charge (PDF BJ / BL) ────────────────────── */}
          <div className={cn(
            'flex items-center justify-between px-3 py-2.5 border-b border-ink-100',
            hasAddl ? 'bg-red-50' : '',
          )}>
            <div>
              <span className={cn('text-xs', hasAddl ? 'text-red-700 font-semibold' : 'text-ink-700')}>
                Additional demand charge
              </span>
              <span className="text-[10px] text-ink-400 ml-1">(₹{DEMAND_PENAL_RS_PER_KVA}/kVA/month)</span>
              {hasAddl && isWb ? (
                <p className="text-[10px] text-red-500 mt-0.5">
                  MD Peak {n2(mdKvaPeak)} kVA − CD {contractDemandKva} kVA = {n2(addlKvaDisplay)} kVA above CD
                </p>
              ) : hasAddl ? (
                <span className="text-[10px] text-red-400 ml-1">
                  · {n2(addlKvaDisplay)} kVA above CD
                </span>
              ) : null}
            </div>
            <span className={cn('num text-xs font-bold', hasAddl ? 'text-red-700' : 'text-ink-900')}>
              {rs(addlDcDisplay)}
            </span>
          </div>

          {/* ── Total demand cost (PDF BM) ────────────────────────────────── */}
          <div className="flex items-center justify-between px-3 py-2.5 bg-ink-50">
            <span className="text-xs font-bold text-ink-900">Total demand cost</span>
            <span className={cn('num text-sm font-extrabold', hasAddl ? 'text-red-700' : 'text-ink-900')}>
              {rs(totalDcDisplay)}
            </span>
          </div>
        </div>
          );
        })()}
      </Card>

      {/* ── Today's Breakdown ──────────────────────────────────────────── */}
      <SectionTitle>Today's Running Cost</SectionTitle>
      <Card padded>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-sm font-bold text-ink-900 leading-tight">Intraday Bill</p>
            <p className="text-[10px] text-ink-400 mt-0.5">Since midnight IST · updates every 5 min</p>
          </div>
          <div className="text-right shrink-0">
            <p className="num text-xl font-extrabold text-ink-900 leading-tight">{rs(dailyCostRs)}</p>
            <p className="text-[9px] text-ink-400">net today</p>
          </div>
        </div>

        {/* Hourly bar chart */}
        {hourlyCost.length > 0 && (() => {
          const maxCost = Math.max(...hourlyCost.map(h => h.normal + h.peak + h.offPeak), 1);
          const hScale  = getNiceAxisScale(0, maxCost);
          return (
            <div className="h-28 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyCost} margin={{ top: 4, right: 4, bottom: 0, left: -24 }} barSize={6}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 8 }} tickLine={false} axisLine={false} interval={3} />
                  <YAxis tick={{ fontSize: 8 }} tickLine={false} axisLine={false}
                    domain={[hScale.min, hScale.max]} ticks={hScale.ticks}
                    tickFormatter={v => `₹${v >= 1000 ? `${(v/1000).toFixed(0)}k` : Math.round(v)}`} />
                  <Tooltip content={<RsTooltip />} />
                  <Bar dataKey="normal"  name="Normal"   stackId="a" fill={TOD_COLORS['NORMAL']}   radius={[0,0,0,0]} />
                  <Bar dataKey="peak"    name="Peak"     stackId="a" fill={TOD_COLORS['PEAK']}     radius={[0,0,0,0]} />
                  <Bar dataKey="offPeak" name="Off-Peak" stackId="a" fill={TOD_COLORS['OFF-PEAK']} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* Breakdown rows — styled table */}
        <div className="rounded-xl border border-ink-100 overflow-hidden">
          {/* Energy row — always shown */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-ink-100">
            <div>
              <p className="text-xs font-semibold text-ink-800">Energy delivered</p>
              <p className="text-[10px] text-ink-400">{formatKwh(dailyEnergyKwh)}</p>
            </div>
            <span className="num text-xs font-bold text-ink-900">{rs(dailyEnergyCostRs)}</span>
          </div>

          {/* Power Factor adjustment — WB only (hasPfAdjustment gate) */}
          {tariff.hasPfAdjustment && (
            <div className={cn(
              'flex items-center justify-between px-3 py-2.5 border-b',
              displayDailyPfRebateRs > 0 ? 'bg-emerald-50/60 border-emerald-100' :
              displayDailyPfPenaltyRs > 0 ? 'bg-red-50/60 border-red-100' : 'border-ink-100',
            )}>
              <div>
                <p className={cn(
                  'text-xs font-semibold',
                  displayDailyPfRebateRs > 0 ? 'text-emerald-800' :
                  displayDailyPfPenaltyRs > 0 ? 'text-red-700' : 'text-ink-800',
                )}>
                  Power Factor {displayDailyPfRebateRs > 0 ? 'rebate' : displayDailyPfPenaltyRs > 0 ? 'penalty' : 'adjustment'}
                </p>
                <p className={cn(
                  'text-[10px]',
                  displayDailyPfRebateRs > 0 ? 'text-emerald-600' :
                  displayDailyPfPenaltyRs > 0 ? 'text-red-500' : 'text-ink-400',
                )}>
                  {dailyPfAvg > 0 && (
                    <span className={cn(
                      'num font-bold mr-1.5',
                      dailyPfAvg >= 0.95 ? 'text-emerald-600' :
                      dailyPfAvg < 0.80  ? 'text-red-600'     : 'text-ink-600',
                    )}>
                      PF {formatPF(dailyPfAvg)}
                    </span>
                  )}
                  {displayDailyPfRebateRs > 0 ? '· saving deducted today' :
                   displayDailyPfPenaltyRs > 0 ? '· added to bill today' : 'no adjustment today'}
                </p>
              </div>
              <span className={cn(
                'num text-xs font-bold',
                displayDailyPfRebateRs > 0 ? 'text-emerald-600' :
                displayDailyPfPenaltyRs > 0 ? 'text-red-600' : 'text-ink-400',
              )}>
                {displayDailyPfRebateRs > 0 ? `-${rs(displayDailyPfRebateRs)}` :
                 displayDailyPfPenaltyRs > 0 ? `+${rs(displayDailyPfPenaltyRs)}` : '—'}
              </span>
            </div>
          )}

          {/* Load Factor adjustment — WBSEDCL only */}
          {tariff.hasLfRebate && (
            <div className={cn(
              'flex items-center justify-between px-3 py-2.5 border-b',
              displayDailyLfRebateRs > 0   ? 'bg-emerald-50/60 border-emerald-100' :
              displayDailyLfSurchargeRs > 0 ? 'bg-red-50/60 border-red-100' : 'border-ink-100',
            )}>
              <div>
                <p className={cn(
                  'text-xs font-semibold',
                  displayDailyLfRebateRs > 0   ? 'text-emerald-800' :
                  displayDailyLfSurchargeRs > 0 ? 'text-red-700' : 'text-ink-800',
                )}>
                  Load Factor {displayDailyLfRebateRs > 0 ? 'rebate' : displayDailyLfSurchargeRs > 0 ? 'surcharge' : 'rebate'}
                </p>
                <p className={cn(
                  'text-[10px]',
                  displayDailyLfRebateRs > 0   ? 'text-emerald-600' :
                  displayDailyLfSurchargeRs > 0 ? 'text-red-500' : 'text-ink-400',
                )}>
                  {dailyLfPct > 0 && (
                    <span className={cn(
                      'num font-bold mr-1.5',
                      dailyLfPct >= 70 ? 'text-emerald-600' :
                      dailyLfPct >= 65 ? 'text-ink-600'    : 'text-red-600',
                    )}>
                      LF {dailyLfPct.toFixed(1)}%
                    </span>
                  )}
                  {displayDailyLfRebateRs > 0   ? '· saving deducted today' :
                   displayDailyLfSurchargeRs > 0 ? '· surcharge added today' : 'not yet applicable today'}
                </p>
              </div>
              <span className={cn(
                'num text-xs font-bold',
                displayDailyLfRebateRs > 0   ? 'text-emerald-600' :
                displayDailyLfSurchargeRs > 0 ? 'text-red-600' : 'text-ink-400',
              )}>
                {displayDailyLfRebateRs > 0   ? `-${rs(displayDailyLfRebateRs)}` :
                 displayDailyLfSurchargeRs > 0 ? `+${rs(displayDailyLfSurchargeRs)}` : '—'}
              </span>
            </div>
          )}

          {/* Net today — always shown */}
          <div className="flex items-center justify-between px-3 py-3 bg-ink-50">
            <span className="text-xs font-bold text-ink-900">Net cost today</span>
            <span className="num text-sm font-extrabold text-ink-900">{rs(dailyCostRs)}</span>
          </div>
        </div>
      </Card>

      {/* ── Projected Bill (merged: full invoice + month-end projection) ── */}
      <SectionTitle>Projected Bill · Month-End Estimate</SectionTitle>
      <Card padded>
        <CardHeader
          title="Projected Bill"
          description={`Based on ${daysPassed}-day run rate · ${daysRemaining} days remaining`}
          trailing={<span className="num text-sm font-bold text-ink-900">{rs(projectedNetPayable)}</span>}
        />

        {/* Month-end projection breakdown */}
        <div className="space-y-0.5">
          <BillRow label="Projected energy charge (gross)" value={rs(projectedGrossEnergy)} bold />
          {tariff.hasPfAdjustment && (
            <BillRow
              label={projectedPfAdjRs > 0 ? 'PF Rebate (projected)' : projectedPfAdjRs < 0 ? 'PF Penalty (projected)' : 'PF Adjustment (projected)'}
              value={projectedPfAdjRs > 0 ? `-${rs(projectedPfAdjRs)}` : projectedPfAdjRs < 0 ? `+${rs(Math.abs(projectedPfAdjRs))}` : rs(0)}
              indent
              savings={projectedPfAdjRs > 0}
              penalty={projectedPfAdjRs < 0}
              sub={projectedPfAdjRs > 0 ? 'projected rebate' : projectedPfAdjRs < 0 ? 'projected penalty' : 'no adjustment'}
            />
          )}
          {tariff.hasLfRebate && (
            <BillRow
              label={projectedLfAdjRs > 0 ? 'LF Rebate (projected)' : projectedLfAdjRs < 0 ? 'LF Penalty (projected)' : 'LF Adjustment (projected)'}
              value={projectedLfAdjRs > 0 ? `-${rs(projectedLfAdjRs)}` : projectedLfAdjRs < 0 ? `+${rs(Math.abs(projectedLfAdjRs))}` : rs(0)}
              indent
              savings={projectedLfAdjRs > 0}
              penalty={projectedLfAdjRs < 0}
              sub={projectedLfAdjRs > 0 ? 'projected rebate' : projectedLfAdjRs < 0 ? 'projected surcharge' : 'no adjustment'}
            />
          )}
          {(() => {
            // WB: use engine's ROUNDED values (same as Max Demand Analysis card and PDF).
            //   normalDcProj = breakdown.dcNormalRs  (BK = chargeableKva × 270, integer)
            //   addlDcProj   = breakdown.dcPenalRs   (BL = addlKva × 135, integer)
            //   totalDcProj  = breakdown.dcRs        (BM = BK + BL)
            // This guarantees: normalDcProj + addlDcProj == totalDcProj always.
            // The previous code used unrounded float kVA for the line items
            // (peakKvaProj = maxDemandKva = 350.60) but breakdown.dcRs (rounded, 1,15,155)
            // for the total — so 94,661.65 + 23,570.82 ≠ 1,15,155 on screen.
            //
            // TGSPDCL: unchanged — single "Maximum demand" line from totalDemandChargeRs.
            const isWbProj     = tariff.energyUnit !== 'kvah';
            const peakKvaProj  = isWbProj ? breakdown.chargeableKva : chargeableKva;   // WB: rounded engine
            const addlKvaProj  = isWbProj ? breakdown.addlKva       : mdOverrunKva;    // WB: rounded engine
            const normalDcProj = isWbProj ? breakdown.dcNormalRs    : demandChargeRs;  // WB: BK
            const addlDcProj   = isWbProj ? breakdown.dcPenalRs     : mdOverrunPenaltyRs; // WB: BL
            const totalDcProj  = normalDcProj + addlDcProj;  // = breakdown.dcRs (BM) for WB ✓
            const hasAddlProj  = addlKvaProj > 0;

            if (!isWbProj) {
              // TGSPDCL: single consolidated line matching "Total demand cost" in Max Demand Analysis
              return (
                <BillRow
                  label="Maximum demand"
                  value={rs(totalDcProj)}
                  indent
                />
              );
            }

            return (
              <>
                <BillRow
                  label="Demand charge (billing-cycle peak)"
                  value={rs(normalDcProj)}
                  indent
                  sub={`${n2(peakKvaProj)} kVA × ₹${DEMAND_CHARGE_RS_PER_KVA}`}
                />
                {hasAddlProj && (
                  <BillRow
                    label="Additional demand charge"
                    value={rs(addlDcProj)}
                    indent
                    penalty
                    sub={`${n2(addlKvaProj)} kVA above CD × ₹${DEMAND_PENAL_RS_PER_KVA}`}
                  />
                )}
                {hasAddlProj && (
                  <BillRow label="Total demand cost" value={rs(totalDcProj)} indent bold penalty />
                )}
              </>
            );
          })()}
          <BillRow label="Electricity Duty (projected)" value={rs(projectedEdRs)} indent
            sub={tariff.electricityDutyModel === 'pct-of-bill'
              ? `${(tariff.electricityDutyValue * 100).toFixed(1)}% of bill`
              : `₹${tariff.electricityDutyValue.toFixed(2)}/kVAh`} />
          {meterRentalRs > 0 && (
            <BillRow label="Meter rental" value={rs(meterRentalRs)} indent sub="/month" />
          )}
          {customerChargesRs > 0 && (
            <BillRow label="Customer charges" value={rs(customerChargesRs)} indent sub="/month" />
          )}
          {TPR_PCT > 0 && (
            <BillRow
              label={`Timely Payment Rebate (${TPR_PCT.toFixed(2)}%)`}
              value={`-${rs(Math.abs(projectedTprProjected))}`}
              indent savings sub="if paid by due date"
            />
          )}

          <div className="h-px bg-ink-200 my-2" />

          {/* Net payable */}
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-ink-50 border border-ink-200">
            <span className="text-sm font-bold text-ink-900">Projected net payable by due date</span>
            <span className="num text-lg font-bold text-ink-900">{rs(projectedNetPayable)}</span>
          </div>
        </div>

        <div className="flex items-start gap-2 mt-3 p-2.5 rounded-xl bg-blue-50 border border-blue-100">
          <Info className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-blue-700 leading-snug">
            Projection uses a linear run-rate from {daysPassed} days of actual data.
            Demand charge is fixed from the billing cycle peak.
            PF rebate and LF rebate/surcharge are projected at current rates.
          </p>
        </div>
      </Card>

      {/* ── PDF Download ───────────────────────────────────────────────── */}
      <SectionTitle>Download Bill</SectionTitle>

      {(() => {
        // ── Current-month PDF data (assembled inline, mirrors existing props) ──
        const currentPdfData: BillPdfData & { discLabel: string } = {
          stateCode: tariff.stateCode,
          discLabel: `${tariff.discName} ${tariff.tariffCategory}`,
          billingMonth: format(startOfMonth(dataEnd), 'MMM, yyyy').toUpperCase(),
          billingDate: format(dataEnd, 'dd.MM.yyyy'),
          // Reading-row dates come from the billing month, not "today".
          readingFromDate: format(startOfMonth(dataEnd), 'dd-MMM-yy'), // opening (01-Jun-26)
          readingToDate:   format(endOfMonth(dataEnd),   'dd-MMM-yy'), // closing (30-Jun-26)
          invoiceNo: `MTD-${format(dataEnd, 'yyyyMM')}-${selectedSheet?.meta?.consumerId ?? '000'}`,
          consumerName: selectedSheet?.label ?? 'Consumer',
          consumerId: selectedSheet?.meta?.consumerId ?? '—',
          installationNo: selectedSheet?.id ?? '—',
          serviceAddress: selectedSheet?.meta?.location ?? selectedSheet?.label ?? '—',
          meterNo: latest?.meterSerial ?? '—',
          tariffCode: selectedSheet?.meta?.tariffCode ?? tariff.tariffCategory,
          supplyVoltageKv: tariff.voltageLevel.replace(/[^0-9.]/g, '') || '0.40',
          contractDemandKva,
          panNo: '—',
          feeder: selectedSheet?.meta?.feeder ?? '—',
          category: selectedSheet?.meta?.category ?? tariff.tariffCategory,
          payableDate: format(dataEnd, 'dd.MM.yyyy'),
          disconnectionDate: format(dataEnd, 'dd.MM.yyyy'),
          readingCurrentKwh: latest?.energyKwh ?? 0,
          readingPreviousKwh: billingRows.length > 1 ? billingRows[0]?.energyKwh ?? 0 : 0,
          readingCurrentKvah: latest?.energyVAh ?? 0,
          readingPreviousKvah: billingRows.length > 1 ? billingRows[0]?.energyVAh ?? 0 : 0,
          differenceKwh: tariff.energyUnit === 'kvah'
            // TGSPDCL: use billing engine kWh (same source as dashboard "This Month" kWh)
            ? breakdown.periods.reduce((s, p) => s + p.kwh, 0)
            // WB: register delta (current - billing month start)
            : (latest ? (latest.energyKwh ?? 0) - (billingRows.length > 1 ? billingRows[0]?.energyKwh ?? 0 : 0) : 0),
          differenceKvah: latest ? (latest.energyVAh ?? 0) - (billingRows.length > 1 ? billingRows[0]?.energyVAh ?? 0 : 0) : 0,
          multiplyingFactor: 1,
          consumptionKwh: breakdown.totalKvah,
          consumptionKvah: breakdown.totalKvah,
          consumptionKva: breakdown.peakKva,
          tod1Kvah: tariff.energyUnit === 'kvah' ? bkTodPeakKvah   : bkKwhPeak,   // For TGSPDCL: actual MTD Peak kVAh; WB: per-period kWh
          tod2Kvah: tariff.energyUnit === 'kvah' ? bkTodOffpeakKvah : bkKwhOffpeak, // For TGSPDCL: actual MTD Off-Peak kVAh; WB: per-period kWh
          rateNormal: TARIFF_RATES.NORMAL,
          ratePeak: TARIFF_RATES.PEAK,
          rateOffpeak: TARIFF_RATES['OFF-PEAK'],
          // TGSPDCL: todChargeRs drives the "TOD Charges" line in the PDF rate table.
          // = Peak Penalty + Off-Peak Rebate combined (net ToD adjustment).
          // Positive = net penalty; negative = net rebate. For display, PDF shows separately.
          todChargeRs: tariff.energyUnit === 'kvah'
            ? bkTodPeakPenaltyRs - bkTodOffpeakRebateRs
            : 0,
          todKvah: breakdown.totalKvah,
          // Telangana ToD breakdown — pass through to PDF for correct line-item display
          bkEcBaseRs: tariff.energyUnit === 'kvah' ? bkEcBaseRs : undefined,
          bkTodPeakKvah: tariff.energyUnit === 'kvah' ? bkTodPeakKvah : undefined,
          bkTodOffpeakKvah: tariff.energyUnit === 'kvah' ? bkTodOffpeakKvah : undefined,
          bkTodPeakPenaltyRs: tariff.energyUnit === 'kvah' ? bkTodPeakPenaltyRs : undefined,
          bkTodOffpeakRebateRs: tariff.energyUnit === 'kvah' ? bkTodOffpeakRebateRs : undefined,
          // Pass explicit MTD total kVAh so PDF's mtdTotalKvah equals dashboard's This Month kVAh
          bkTotalKvah: tariff.energyUnit === 'kvah' ? breakdown.totalKvah : undefined,
          energyChargeNormal,
          energyChargePeak,
          energyChargeOffpeak,
          totalEnergyCharge,
          kwhNormal: pdfKwhNormal,
          kwhPeak: pdfKwhPeak,
          kwhOffpeak: pdfKwhOffpeak,
          pfAvg,
          pfRebatePct,
          pfRebatePctNormal,
          pfRebatePctPeak,
          pfRebatePctOffpeak,
          pfRebateCost,
          lfPct,
          lfRebateCost,
          maxDemandKva,
          // chargeableKva, demandChargeRs, mdOverrunKva, mdOverrunPenaltyRs:
          // Both TGSPDCL and WB now use the billing engine breakdown as single source
          // of truth (fixed above). The ternary is no longer needed — both states
          // already use chargeableKva = breakdown.chargeableKva/normalKva etc.
          chargeableKva,       // = breakdown.chargeableKva (WB) or breakdown.normalKva (TG)
          demandChargeRs,      // = breakdown.dcRs
          mdOverrunKva,        // = breakdown.addlKva
          mdOverrunPenaltyRs,  // = breakdown.dcPenalRs
          normalKva: chargeableKva,   // alias used by TGSPDCL PDF
          addlKva:   mdOverrunKva,    // alias used by TGSPDCL PDF
          // Per-ToD Max Demand kVA from billing engine (same for both states).
          maxDemandKvaNormal:  breakdown.mdKvaNormal  ?? (latest?.maxDemandKvaNormal  ?? 0),
          maxDemandKvaPeak:    breakdown.mdKvaPeak    ?? (latest?.maxDemandKvaPeak    ?? 0),
          maxDemandKvaOffpeak: breakdown.mdKvaOffpeak ?? (latest?.maxDemandKvaOffpeak ?? 0),
          grossBeforeEd,
          subTotalRs,
          electricityDutyRs,
          colonyChargesRs: 0,
          lfChargesRs: 0,
          customerChargesRs: breakdown.customerChargesRs,
          acdSurchargeRs: breakdown.acdSurchargeRs,
          uiChargesRs: 0,
          crossSubsidyRs: 0,
          addlSurchargeRs: 0,
          latePaymentRs: 0,
          interestOnEdRs: 0,
          incTod1Rs: 0,
          incTod2Rs: 0,
          wheelingChargesRs: 0,
          transmissionRs: 0,
          gridSupportRs: 0,
          otherCharges1Rs: 0,
          otherCharges2Rs: 0,
          grossTotalRs: grossPayable,
          otherCreditAdjRs: 0,
          meterRentalRs,
          grossPayable,
          grossPayableRs: grossPayable,
          tprRs,
          netPayable,
          projectedNetPayable,
          daysPassed,
          daysInMonth,
          peakDemandTimestamp: maxDemandRow ? format(maxDemandRow.timestamp, 'dd MMM yyyy, hh:mm a') : undefined,
          totalAmountPayableRs: netPayable,
          totalArrearsRs: 0,
          netBillAmountRs: netPayable,
          projectedGrossEcRs: projectedGrossEnergy,
          projectedPfAdjRs,
          projectedLfAdjRs,
          projectedEdRs,
          projectedTprRs: projectedTprProjected,
          projectedKvahNormal,
          projectedKvahPeak,
          projectedKvahOffpeak,

          // ── West Bengal (WBSEDCL) specific fields ─────────────────────
          // Telangana renderer ignores all wbXxx fields.
          ...(isWb ? {
            // Previous Reading Date = first day of billing month (01-MMM-YYYY)
            wbPreviousReadingDate: format(startOfMonth(dataEnd), 'dd-MMM-yyyy'),

            // Per-period average PF — use derivedEngine's runningAvgPFNormal/Peak/Offpeak
            // (ΣWhNormal / ΣVAhNormal etc.) from the latest row. These are the correct
            // billing-cycle PF values per period. The billing engine's breakdown.periods[p].pfAvg
            // is wrong for WB because billing.ts weightedPF() divides kWh by raw VAh
            // (energyVAh = vahReceived in VAh units), yielding PF ≈ 0.001 instead of ~0.961.
            // The derivedEngine computes it correctly as billingWh/billingVAh (same units).
            // Fall back to overall runningAvgPF, then pfAvg, then 0.96 as last resort.
            wbPfAvgNormal:  latest?.runningAvgPFNormal  || latest?.runningAvgPF || pfAvg || 0.96,
            wbPfAvgPeak:    latest?.runningAvgPFPeak    || latest?.runningAvgPF || pfAvg || 0.96,
            wbPfAvgOffpeak: latest?.runningAvgPFOffpeak || latest?.runningAvgPF || pfAvg || 0.96,

            // Per-period MTD kWh from billing engine = "This Month" dashboard values.
            // Same source as the dashboard Energy Charges card (bkKwhNormal/Peak/Offpeak).
            wbKwhNormal:  bkKwhNormal,
            wbKwhPeak:    bkKwhPeak,
            wbKwhOffpeak: bkKwhOffpeak,

            // Per-period kWh at billing month START (first row of billingRows).
            // "Previous Reading" in the meter table.
            // Reading Advance = Present (wbKwh*) − Previous (wbPrevKwh*).
            wbPrevKwhNormal:  (() => {
              const firstRow = billingRows.length > 0 ? billingRows[0] : null;
              if (!firstRow) return 0;
              // Per-period kWh at month start — derive from energy register delta from
              // the very first row. Since it's the start, the "previous" value is the
              // same as the first row's cumulative kWh register, split by period using
              // the proportion of kWh consumed: firstRow.energyKwh * fractionNormal.
              // However, we don't have per-period cumulative registers. Instead, we
              // estimate previous-month endpoint as current minus MTD:
              //   prevReading_normal = currentReading_normal - MTD_normal
              // currentReading_normal ≈ (readingCurrentKwh) * (bkKwhNormal / totalKwh_billing)
              // This matches: Present − Previous = MTD kWh by definition.
              const totalMtdKwh = bkKwhNormal + bkKwhPeak + bkKwhOffpeak;
              if (totalMtdKwh <= 0) return 0;
              const currentKwh = latest?.energyKwh ?? 0;
              const frNormal   = bkKwhNormal / totalMtdKwh;
              return Math.max(0, currentKwh * frNormal - bkKwhNormal);
            })(),
            wbPrevKwhPeak: (() => {
              const totalMtdKwh = bkKwhNormal + bkKwhPeak + bkKwhOffpeak;
              if (totalMtdKwh <= 0) return 0;
              const currentKwh = latest?.energyKwh ?? 0;
              const frPeak     = bkKwhPeak / totalMtdKwh;
              return Math.max(0, currentKwh * frPeak - bkKwhPeak);
            })(),
            wbPrevKwhOffpeak: (() => {
              const totalMtdKwh = bkKwhNormal + bkKwhPeak + bkKwhOffpeak;
              if (totalMtdKwh <= 0) return 0;
              const currentKwh  = latest?.energyKwh ?? 0;
              const frOffpeak   = bkKwhOffpeak / totalMtdKwh;
              return Math.max(0, currentKwh * frOffpeak - bkKwhOffpeak);
            })(),

            // Per-period max demand kVA from billing engine — same as dashboard display.
            wbMdKvaNormal:  breakdown.mdKvaNormal,
            wbMdKvaPeak:    breakdown.mdKvaPeak,
            wbMdKvaOffpeak: breakdown.mdKvaOffpeak,
          } : {}),
        };

        const currentMonthLabel = format(startOfMonth(dataEnd), 'MMMM yyyy').toUpperCase();
        const hasPrev = prevMonthSufficient && prevPdfData !== null;

        return (
          <BillDownloadDropdown
            currentLabel={currentMonthLabel}
            currentData={currentPdfData}
            prevLabel={prevMonthLabel}
            prevData={hasPrev ? prevPdfData! : null}
            prevDayCount={prevMonthDayCount}
            prevDaysInMonth={prevDaysInMonth}
            minDays={PREV_MONTH_MIN_DAYS}
          />
        );
      })()}

      <p className="text-[10px] text-ink-400 text-center pb-2 leading-relaxed px-4">
        All energy/PF/LF values computed by the Cortex Pi gateway per VARIABLES.pdf §2.3 &amp; §3.
        Demand, ED, rental, and TPR computed in the browser from WBSEDCL tariff constants.
      </p>
    </div>
  );
}